import React, { useState, useEffect, useRef } from 'react';
import { useCart } from '../contexts/CartContext';
import {
  shippingService,
  paymentService,
  orderService,
  couponService,
  sanitizePseReturnQueryString
} from '../services/api';
import './Checkout.css';
import './OrderSuccessJaviland.css';

/** Etiqueta de método de pago para pantallas post-compra (marca Javiland) */
function getPaymentMethodLabel(method) {
  switch (method) {
    case 'wompi_card_credit':
      return 'Tarjeta crédito (Wompi)';
    case 'wompi_card_debit':
      return 'Tarjeta débito (Wompi)';
    case 'wompi_pse':
      return 'PSE — transferencia desde tu banco (Wompi)';
    case 'wompi_nequi':
      return 'Nequi (Wompi)';
    case 'wompi_daviplata':
      return 'Daviplata (Wompi)';
    case 'wompi_bancolombia_transfer':
      return 'Botón Bancolombia / transferencias (Wompi)';
    case 'wompi_cash_collect':
      return 'Efectivo — corresponsal Bancolombia (Wompi)'; // órdenes antiguas
    case 'pse':
      return 'PSE';
    case 'cash':
      return 'Efectivo / red de pagos';
    case 'debit_card':
      return 'Tarjeta débito';
    case 'credit_card':
    default:
      return 'Tarjeta — Wompi';
  }
}

function isWompiCard(method) {
  return method === 'wompi_card_credit' || method === 'wompi_card_debit';
}

function formatConfirmedDateCol(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

/** Imagen de línea en resumen post-compra (URL absoluta o ruta bajo `/`). */
function normalizeOrderLineImage(url) {
  if (url == null) return '/images/placeholders/placeholder-product.png';
  const s = String(url).trim();
  if (!s) return '/images/placeholders/placeholder-product.png';
  if (s.startsWith('http://') || s.startsWith('https://')) return s;
  if (s.startsWith('/')) return s;
  return `/${s.replace(/^\/+/, '')}`;
}

/**
 * Orden completa desde GET /api/orders/:id (`data.order`) → estado `completedOrder` del checkout.
 */
function buildCompletedOrderFromApiOrder(fullOrder, overrides = {}) {
  if (!fullOrder || typeof fullOrder !== 'object') return null;

  const itemsRaw = Array.isArray(fullOrder.items) ? fullOrder.items : [];
  const itemsSnapshot = itemsRaw.map((it) => ({
    id: it.productId ?? it.product_id,
    name: it.productName ?? it.product_name ?? it.name ?? 'Producto',
    image: normalizeOrderLineImage(it.productImageUrl ?? it.image_url),
    quantity: Number(it.quantity ?? 1) || 1,
    unitPrice: Number(it.productPrice ?? it.product_price ?? it.price ?? 0),
    selectedSize: it.selectedSize ?? it.selected_size,
    selectedColor: it.selectedColor ?? it.selected_color
  }));

  const city = fullOrder.shipping_city != null ? String(fullOrder.shipping_city).trim() : '';
  const dept = fullOrder.shipping_department != null ? String(fullOrder.shipping_department).trim() : '';
  const addr = fullOrder.shipping_address != null ? String(fullOrder.shipping_address).trim() : '';
  const zip = fullOrder.shipping_zip_code != null ? String(fullOrder.shipping_zip_code).trim() : '';
  const recipient = fullOrder.customer_name != null ? String(fullOrder.customer_name).trim() : '';
  const idType =
    fullOrder.customer_identification_type != null ? String(fullOrder.customer_identification_type).trim() : '';
  const idNum =
    fullOrder.customer_identification_number != null ? String(fullOrder.customer_identification_number).trim() : '';
  const docLine = [idType, idNum].filter(Boolean).join(' ').trim();

  const wompiKey =
    overrides.paymentMethodKey ??
    (typeof fullOrder.wompi_payment_method === 'string' && fullOrder.wompi_payment_method.trim() !== ''
      ? fullOrder.wompi_payment_method.trim()
      : undefined);

  return {
    orderId: fullOrder.id != null ? Number(fullOrder.id) : overrides.orderId,
    orderNumber: fullOrder.order_number ?? overrides.orderNumber,
    total: Number(fullOrder.total ?? overrides.total ?? 0),
    paymentStatus: overrides.paymentStatus ?? 'paid',
    paymentStatusDetail: overrides.paymentStatusDetail ?? null,
    paymentMethodKey: wompiKey,
    paymentMethodLabel:
      overrides.paymentMethodLabel ??
      (wompiKey ? getPaymentMethodLabel(wompiKey) : 'Wompi'),
    itemsSnapshot,
    subtotal: Number(fullOrder.subtotal ?? 0),
    shipping: Number(fullOrder.shipping ?? 0),
    taxAmount: Number(fullOrder.tax ?? 0),
    shippingRecipientName: recipient,
    shippingIdentification: docLine,
    shippingCity: city,
    shippingDepartment: dept,
    shippingCountry: 'Colombia',
    shippingAddressLine: addr || [city, dept].filter(Boolean).join(', ') || '—',
    shippingZip: zip,
    shippingExtra: '',
    customerEmail:
      fullOrder.customer_email != null
        ? String(fullOrder.customer_email).trim()
        : overrides.customerEmail || '',
    confirmedAt: fullOrder.updated_at ?? new Date().toISOString()
  };
}

/** Campos de envío/recipient desde el wizard del checkout (mientras no haya orden completa desde API). */
function buildShippingSnapshotFromCheckoutForm(formData) {
  const recipient = `${formData.firstName || ''} ${formData.lastName || ''}`.trim();
  const idType = (formData.identificationType || '').trim();
  const idNum = String(formData.identificationNumber || '').replace(/[\s-]/g, '').trim();
  const docLine = [idType, idNum].filter(Boolean).join(' ').trim();
  const addr = (formData.address || '').trim();
  const city = (formData.city || '').trim();
  const dept = (formData.department || '').trim();
  const country = ((formData.country || '').trim() || 'Colombia');
  const zip = (formData.zipCode || '').trim();
  const extra = (formData.shippingAdditionalInfo || '').trim();
  const lineMain = addr !== '' ? addr : [city, dept].filter(Boolean).join(', ');
  return {
    shippingRecipientName: recipient,
    shippingIdentification: docLine,
    shippingCity: city,
    shippingDepartment: dept,
    shippingCountry: country,
    shippingAddressLine: lineMain !== '' ? lineMain : '—',
    shippingZip: zip,
    shippingExtra: extra
  };
}

/** payment_method_id para API de cuotas MP (misma heurística que al enviar el pago). */
function detectPaymentMethodIdFromPan(panDigits) {
  const n = String(panDigits || '').replace(/\D/g, '');
  if (n.length < 1) return 'visa';
  const firstDigit = n.charAt(0);
  const firstTwo = n.substring(0, 2);
  if (firstDigit === '4') return 'visa';
  if (firstDigit === '5' || ['50', '51', '52', '53', '54', '55'].includes(firstTwo)) return 'master';
  if (firstTwo === '34' || firstTwo === '37') return 'amex';
  if (firstTwo === '30' || firstTwo === '36' || firstTwo === '38') return 'diners';
  return 'visa';
}

/**
 * MP suele devolver un texto genérico con enlace a developers cuando falla tokenización
 * (404, credenciales, tarjeta vencida, etc.). Normalizamos el mensaje para el usuario.
 */
function formatMercadoPagoClientError(raw) {
  if (raw == null || raw === '') return raw;
  let s = String(raw).trim();
  s = s.replace(/\s*\(https?:\/\/[^)]+\)/gi, '').replace(/https?:\/\/\S+/gi, '').replace(/\s{2,}/g, ' ').trim();
  if (/recursos de la API|Sitio de Desarrolladores|developers\.mercadopago/i.test(s)) {
    return (
      'Mercado Pago rechazó la tokenización (a veces muestra un mensaje genérico). ' +
      'En modo prueba Colombia usa números oficiales de MP: Mastercard 5031 7557 3453 0604 o Visa 4509 9535 6623 3704; ' +
      'titular APRO, CVV 123, vencimiento MMAA futuro (ej. 1130). ' +
      'Las tarjetas 5254… u otras de “prueba genérica” suelen fallar aunque el vencimiento sea válido.'
    );
  }
  return s || String(raw).trim();
}

function getWompiBaseUrl(env) {
  return env === 'production' ? 'https://production.wompi.co/v1' : 'https://sandbox.wompi.co/v1';
}

/**
 * URL de pasarela/async (backend: sendSuccess → data.redirectUrl desde async_payment_url de Wompi).
 */
function resolveCheckoutWompiRedirectUrl(paymentResponse, pseDiag = false) {
  if (!paymentResponse) return '';
  const d = paymentResponse.data !== undefined ? paymentResponse.data : paymentResponse;
  if (!d || typeof d !== 'object') return '';
  const redirectUrl =
    (typeof d.redirectUrl === 'string' ? d.redirectUrl.trim() : '') ||
    (typeof d.redirect_url === 'string' ? d.redirect_url.trim() : '') ||
    (typeof d.async_payment_url === 'string' ? d.async_payment_url.trim() : '') ||
    '';
  if (pseDiag) {
    console.log('PSE full response:', JSON.stringify(paymentResponse));
    console.log('PSE redirectUrl:', redirectUrl);
  }
  return redirectUrl;
}

/**
 * PSE: la URL para ir al banco (ACH) no debe ser la del propio sitio; Wompi a veces devuelve primero redirect_url del comercio.
 */
function resolvePseBankRedirectUrl(paymentResponse) {
  const raw = resolveCheckoutWompiRedirectUrl(paymentResponse, true);
  if (!raw || !/^https?:\/\//i.test(raw)) {
    return '';
  }
  if (typeof window === 'undefined') {
    return raw;
  }
  try {
    const target = new URL(raw);
    if (target.origin === window.location.origin) {
      console.warn(
        'Wompi (async): redirección ignorada al mismo sitio (probable redirect_url del comercio, no la pasarela).',
        raw
      );
      return '';
    }
  } catch {
    return raw;
  }
  return raw;
}

/** PSE y Botón Bancolombia: redirect a pasarela bancaria (async_payment_url). Nequi va aparte (solo push, sin URL). */
const WOMPI_ASYNC_BANK_REDIRECT_METHODS = Object.freeze(['wompi_pse', 'wompi_bancolombia_transfer']);

/** Nequi: solo dígitos; quitar prefijo 57 mientras sobren caracteres; debe quedar 10 dígitos (3xxxxxxxxx). */
function normalizeNequiPhone10Digits(raw) {
  let d = String(raw ?? '').replace(/\D/g, '');
  while (d.length > 10 && d.startsWith('57')) {
    d = d.slice(2);
  }
  return d;
}

function isNequiPhone10Valid(digits) {
  return /^\d{10}$/.test(digits);
}

/**
 * Lista bancos PSE desde GET /api/payments/pse-financial-institutions (sendSuccess puede anidar `data`).
 */
function normalizePseFinancialInstitutionsResponse(resp) {
  if (resp == null) return [];
  const top = resp.data !== undefined ? resp.data : resp;
  const mid = top && typeof top === 'object' && top.data !== undefined ? top.data : top;
  const candidates = [
    mid?.financial_institutions,
    mid?.financialInstitutions,
    mid?.institutions,
    mid?.banks,
    Array.isArray(mid) ? mid : null,
    top?.financial_institutions,
    top?.financialInstitutions
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  if (mid && typeof mid === 'object' && Array.isArray(mid.data)) return mid.data;
  return [];
}

/** Validación documento DIAN · scope personal = paso 1 · billing = factura alternativa paso 2 */
function validateIdentificationFormat(identificationType, rawNumber, scope = 'personal') {
  if (!identificationType) {
    if (scope === 'billing') return 'El tipo de documento del titular de la factura es requerido.';
    if (scope === 'personal' || scope === 'card') return 'Selecciona el tipo de documento.';
    return 'Selecciona el tipo de documento.';
  }
  if (!rawNumber || !String(rawNumber).trim()) {
    if (scope === 'billing') return 'El número de documento del titular de la factura es requerido.';
    if (scope === 'personal' || scope === 'card') return 'El número de documento es requerido.';
    return 'El número de documento es requerido.';
  }
  const idNumber = String(rawNumber).replace(/\s|-/g, '');
  switch (identificationType) {
    case 'CC':
      if (!/^\d{7,10}$/.test(idNumber)) {
        return `La cédula debe tener entre 7 y 10 dígitos (actualmente tiene ${idNumber.length})`;
      }
      break;
    case 'NIT':
      if (!/^\d{9,15}$/.test(idNumber)) {
        return `El NIT debe tener entre 9 y 15 dígitos (actualmente tiene ${idNumber.length})`;
      }
      break;
    case 'CE':
      if (idNumber.length < 5 || idNumber.length > 15) {
        return `La cédula de extranjería debe tener entre 5 y 15 caracteres (actualmente tiene ${idNumber.length})`;
      }
      break;
    case 'TI':
      if (!/^\d{7,10}$/.test(idNumber)) {
        return `La tarjeta de identidad debe tener entre 7 y 10 dígitos (actualmente tiene ${idNumber.length})`;
      }
      break;
    case 'PP':
      if (idNumber.length < 6 || idNumber.length > 15) {
        return `El pasaporte debe tener entre 6 y 15 caracteres (actualmente tiene ${idNumber.length})`;
      }
      break;
    default:
      break;
  }
  return null;
}

function calculateCouponDiscount(coupon, subtotalAmount, shippingAmount) {
  if (!coupon) return 0;
  const minPurchase = Number(coupon.minPurchase || 0);
  if (subtotalAmount < minPurchase) return 0;

  const type = String(coupon.discountType || '').toLowerCase();
  const value = Number(coupon.discountValue || 0);
  const maxDiscount = coupon.maxDiscount != null ? Number(coupon.maxDiscount) : null;

  let discount = 0;
  if (type === 'percentage') {
    discount = subtotalAmount * (value / 100);
    discount = Math.min(discount, subtotalAmount);
  } else if (type === 'fixed') {
    discount = Math.min(value, subtotalAmount + shippingAmount);
  } else if (type === 'shipping_free') {
    discount = shippingAmount;
  }

  if (Number.isFinite(maxDiscount) && maxDiscount > 0) {
    discount = Math.min(discount, maxDiscount);
  }

  return Math.max(0, discount);
}

/** GET /api/orders/:id → objeto para `completedOrder` (reintenta si ítems aún no están en BD). */
async function fetchCompletedOrderFromApi(orderId, overrides = {}, options = {}) {
  const maxAttempts = Math.max(1, Number(options.maxAttempts) || 1);
  const delayMs = Math.max(0, Number(options.delayMs) || 600);
  let last = null;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, delayMs * attempt));
    }
    try {
      const ordRaw = await orderService.getOrderById(orderId);
      const ordData = unwrapSendSuccessPayload(ordRaw);
      const fullRow = ordData?.order ?? ordData;
      const built = buildCompletedOrderFromApiOrder(fullRow, overrides);
      if (!built) continue;
      const hasItems = Array.isArray(built.itemsSnapshot) && built.itemsSnapshot.length > 0;
      if (hasItems || attempt === maxAttempts - 1) {
        return built;
      }
      last = built;
    } catch (err) {
      if (attempt === maxAttempts - 1) {
        console.error('fetchCompletedOrderFromApi:', err);
      }
    }
  }
  return last;
}

function unwrapSendSuccessPayload(payload) {
  if (payload == null) return null;
  if (payload.success && payload.data !== undefined && payload.data !== null) {
    return payload.data;
  }
  const d = payload.data;
  if (d !== undefined && d !== null && typeof d === 'object') {
    return d;
  }
  return payload && typeof payload === 'object' ? payload : null;
}

/** Respuesta GET /payments/pse-return-status — mismo payload `order` + `payment` */
function classifyPseBrowserReturn(inner) {
  const orderPs = String(inner?.order?.payment_status || '').toLowerCase();
  const paySt = String(inner?.payment?.status || '').toUpperCase();

  const paidOk =
    orderPs === 'paid' || paySt === 'APPROVED' || paySt === 'AUTHORIZED';
  const rejectedOk =
    orderPs === 'rejected' ||
    orderPs === 'failed' ||
    paySt === 'CANCELLED' ||
    ['DECLINED', 'REJECTED', 'VOIDED', 'EXPIRED', 'FAILED', 'ERROR'].includes(paySt);

  if (paidOk) return 'approved';
  if (rejectedOk) return 'rejected';
  return 'pending';
}

/** Claves (sessionStorage + localStorage): Wompi suele omitir id/ref en la URL al volver */
const STORAGE_PSE_TX_ID = 'pse_transaction_id';
const STORAGE_PSE_ORDER_REF = 'pse_order_ref';
const STORAGE_PENDING_CHECKOUT_ORDER = 'javiland_checkout_pending_order';

function writePendingCheckoutOrder(orderId, email, orderNumber = null) {
  const oid = Number(orderId);
  const em = String(email || '').trim().toLowerCase();
  if (!Number.isFinite(oid) || oid <= 0 || em === '') return;
  const ref = orderNumber != null && String(orderNumber).trim() !== '' ? String(orderNumber).trim() : null;
  try {
    sessionStorage.setItem(
      STORAGE_PENDING_CHECKOUT_ORDER,
      JSON.stringify({ orderId: oid, email: em, orderNumber: ref, ts: Date.now() })
    );
  } catch {
    /* noop */
  }
}

function readPendingCheckoutOrder() {
  try {
    const raw = sessionStorage.getItem(STORAGE_PENDING_CHECKOUT_ORDER);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.orderId) return null;
    return parsed;
  } catch {
    return null;
  }
}

function clearPendingCheckoutOrder() {
  try {
    sessionStorage.removeItem(STORAGE_PENDING_CHECKOUT_ORDER);
  } catch {
    /* noop */
  }
}

function writePseReturnBackupStores(transactionId, orderRef) {
  const tid = String(transactionId).trim();
  if (tid === '') return;
  const refStr =
    orderRef != null && String(orderRef).trim() !== '' ? String(orderRef).trim() : null;
  try {
    sessionStorage.setItem(STORAGE_PSE_TX_ID, tid);
    if (refStr) sessionStorage.setItem(STORAGE_PSE_ORDER_REF, refStr);
  } catch {
    /* noop */
  }
  try {
    localStorage.setItem(STORAGE_PSE_TX_ID, tid);
    if (refStr) localStorage.setItem(STORAGE_PSE_ORDER_REF, refStr);
  } catch {
    /* noop */
  }
}

function readPseReturnBackupStores() {
  let tx = '';
  let ref = '';
  try {
    tx =
      String(sessionStorage.getItem(STORAGE_PSE_TX_ID) || '').trim() ||
      String(localStorage.getItem(STORAGE_PSE_TX_ID) || '').trim();
    ref =
      String(sessionStorage.getItem(STORAGE_PSE_ORDER_REF) || '').trim() ||
      String(localStorage.getItem(STORAGE_PSE_ORDER_REF) || '').trim();
  } catch {
    /* noop */
  }
  return { tx, ref };
}

function clearPseReturnBackupStores() {
  try {
    sessionStorage.removeItem(STORAGE_PSE_TX_ID);
    sessionStorage.removeItem(STORAGE_PSE_ORDER_REF);
  } catch {
    /* noop */
  }
  try {
    localStorage.removeItem(STORAGE_PSE_TX_ID);
    localStorage.removeItem(STORAGE_PSE_ORDER_REF);
  } catch {
    /* noop */
  }
}

function hasTxnIdInSearchString(searchRaw) {
  const s =
    typeof searchRaw === 'string' && searchRaw.trim() !== ''
      ? searchRaw.trim().startsWith('?')
        ? searchRaw.trim()
        : `?${searchRaw.trim()}`
      : '';
  if (s === '' || s === '?') return false;
  const p = new URLSearchParams(s);
  const txnKeys = ['id', 'transaction_id', 'transactionId', 'wompi_id', 'wompiId'];
  return txnKeys.some((k) => {
    const v = p.get(k);
    return v != null && String(v).trim() !== '';
  });
}

function hasRefOrOrderKeysInSearchString(searchRaw) {
  const s =
    typeof searchRaw === 'string' && searchRaw.trim() !== ''
      ? searchRaw.trim().startsWith('?')
        ? searchRaw.trim()
        : `?${searchRaw.trim()}`
      : '';
  if (s === '' || s === '?') return false;
  const p = new URLSearchParams(s);
  const refKeys = ['ref', 'reference', 'orderNumber', 'order_reference', 'reference_order'];
  const okRef = refKeys.some((k) => {
    const v = p.get(k);
    return v != null && String(v).trim() !== '';
  });
  const oid = p.get('order_id');
  const okOrderId =
    oid != null && String(oid).trim() !== '';
  return okRef || okOrderId;
}

/** Query para GET pse-return-status + si debe llamarse (incluye /checkout/pse-return?pse=1; id vía cookie en servidor) */
function resolveSearchForPseReturnStatus(loadSearch, pathnameRaw) {
  let qs =
    typeof loadSearch === 'string' && loadSearch.trim() !== '' ? loadSearch.trim() : '';
  if (qs && qs[0] !== '?') qs = `?${qs}`;
  let usedStorageFallback = false;
  const hadTxnInUrlInitially = hasTxnIdInSearchString(qs);

  if (!hadTxnInUrlInitially) {
    try {
      const { tx, ref } = readPseReturnBackupStores();
      if (tx !== '') {
        const np = new URLSearchParams();
        np.set('id', tx);
        if (ref !== '') np.set('ref', ref);
        np.set('pse', '1');
        qs = `?${np.toString()}`;
        usedStorageFallback = true;
      }
    } catch {
      /* noop */
    }
  }

  let shouldFetch =
    hasTxnIdInSearchString(qs) ||
    hasRefOrOrderKeysInSearchString(qs);

  const rawPath =
    pathnameRaw !== undefined && pathnameRaw !== null
      ? String(pathnameRaw).replace(/\/$/, '') || '/'
      : typeof window !== 'undefined'
        ? window.location.pathname.replace(/\/$/, '') || '/'
        : '';

  if (
    !shouldFetch &&
    rawPath === '/checkout/pse-return' &&
    hasPseQueryMarkerInSearch(qs !== '' ? qs : typeof loadSearch === 'string' ? loadSearch : '')
  ) {
    shouldFetch = true;
    if (qs === '' || qs === '?') qs = '?pse=1';
  }

  return { searchStr: qs || '?pse=1', usedStorageFallback, shouldFetch };
}

function hasPseQueryMarkerInSearch(searchRaw) {
  const s =
    typeof searchRaw === 'string' && searchRaw.trim() !== ''
      ? searchRaw.trim().startsWith('?')
        ? searchRaw.trim()
        : `?${searchRaw.trim()}`
      : '';
  if (s === '' || s === '?') return false;
  const v = new URLSearchParams(s).get('pse');
  return v === '1' || v === 'true';
}

/** Retorno PSE: URL/storage o /checkout/pse-return?pse=1 (backend lee cookie javiland_pse_tx) */
function computeCheckoutPseReturnDetect() {
  if (typeof window === 'undefined') return false;
  const raw = window.location.pathname.replace(/\/$/, '') || '/';
  if (raw !== '/checkout/pse-return' && raw !== '/checkout') {
    return false;
  }
  const search = window.location.search || '';
  return resolveSearchForPseReturnStatus(search, raw).shouldFetch;
}

const Checkout = () => {
  // Función helper para navegación (compatible con sistema custom de Javiland)
  const navigateToPage = (path) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  /** Tienda SPA: mismo mecanismo que navigateToPage con scroll arriba (evita quedar abajo del checkout). */
  const navigateToTienda = () => {
    window.history.pushState({}, '', '/tienda');
    window.scrollTo(0, 0);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const {
    items,
    subtotal,
    tax,
    shipping,
    total,
    taxBreakdown,
    shippingInfo,
    updateCalculations,
    setShippingAddress,
    clearCart
  } = useCart();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneCountryCode: '+57',
    phoneNumber: '',
    address: '',
    city: '',
    municipalityDane: '', // código DANE 5 dígitos (Colombia); no mostrar al usuario
    department: '',
    state: '', // Estado/Provincia para países internacionales
    zipCode: '', // Código postal
    shippingAdditionalInfo: '', // Información adicional (Apartamento, Casa, Conjunto, etc.)
    country: 'Colombia', // País seleccionado
    wompiPaymentMethod: 'wompi_card_credit',
    nequiPhone: '',
    pseFinancialInstitutionCode: '',
    pseUserType: '0',
    customerNotes: '', // reservado backend (notes/customer_notes); paso 2 sólo muestra shippingAdditionalInfo
    // Campos de facturación
    identificationNumber: '',
    identificationType: 'CC',
    useDifferentBillingAddress: false,
    billingAddress: '',
    billingCity: '',
    billingDepartment: '',
    billingMunicipalityDane: '',
    billingInvoiceFullName: '',
    billingIdentificationType: 'CC',
    billingIdentificationNumber: ''
  });
  
  // MVP: Solo Colombia por ahora (enviar a otros países será una expansión futura)
  const availableCountries = [
    { code: 'CO', name: 'Colombia', phoneCode: '+57' }
    // Países internacionales deshabilitados temporalmente para MVP
    // Se habilitarán en una futura expansión del negocio
    // { code: 'CA', name: 'Canadá', phoneCode: '+1' },
    // { code: 'US', name: 'Estados Unidos', phoneCode: '+1' },
    // { code: 'MX', name: 'México', phoneCode: '+52' },
    // Suramérica
    // { code: 'AR', name: 'Argentina', phoneCode: '+54' },
    // { code: 'BO', name: 'Bolivia', phoneCode: '+591' },
    // { code: 'BR', name: 'Brasil', phoneCode: '+55' },
    // { code: 'CL', name: 'Chile', phoneCode: '+56' },
    // { code: 'EC', name: 'Ecuador', phoneCode: '+593' },
    // { code: 'GY', name: 'Guyana', phoneCode: '+592' },
    // { code: 'PY', name: 'Paraguay', phoneCode: '+595' },
    // { code: 'PE', name: 'Perú', phoneCode: '+51' },
    // { code: 'SR', name: 'Surinam', phoneCode: '+597' },
    // { code: 'UY', name: 'Uruguay', phoneCode: '+598' },
    // { code: 'VE', name: 'Venezuela', phoneCode: '+58' },
    // Centroamérica (parte del ecosistema occidental)
    // { code: 'PA', name: 'Panamá', phoneCode: '+507' },
    // { code: 'CR', name: 'Costa Rica', phoneCode: '+506' },
    // { code: 'GT', name: 'Guatemala', phoneCode: '+502' },
    // { code: 'HN', name: 'Honduras', phoneCode: '+504' },
    // { code: 'NI', name: 'Nicaragua', phoneCode: '+505' },
    // { code: 'SV', name: 'El Salvador', phoneCode: '+503' },
    // { code: 'BZ', name: 'Belice', phoneCode: '+501' },
    // Caribe (parte del ecosistema occidental)
    // { code: 'DO', name: 'República Dominicana', phoneCode: '+1' },
    // { code: 'CU', name: 'Cuba', phoneCode: '+53' },
    // { code: 'JM', name: 'Jamaica', phoneCode: '+1' },
    // { code: 'HT', name: 'Haití', phoneCode: '+509' },
    // { code: 'TT', name: 'Trinidad y Tobago', phoneCode: '+1' },
  ];
  
  // Manejar cambio de país
  const handleCountryChange = (e) => {
    const selectedCountry = availableCountries.find(c => c.code === e.target.value);
    const newCountry = selectedCountry ? selectedCountry.name : e.target.value;
    const isColombia = selectedCountry && selectedCountry.code === 'CO';
    
    setFormData(prev => ({
      ...prev,
      country: newCountry,
      phoneCountryCode: selectedCountry ? selectedCountry.phoneCode : '+57',
      // Limpiar campos dependientes del país cuando cambia
      ...(prev.country !== newCountry ? {
        department: isColombia ? prev.department : '',
        state: isColombia ? '' : prev.state,
        city: '',
        municipalityDane: '',
        // Mantener información adicional y zipCode si el usuario ya los llenó
        // billingDepartment: isColombia ? prev.billingDepartment : '',
        // billingCity: ''
      } : {})
    }));
    
    // Si cambia a Colombia, los departamentos se cargarán automáticamente por el useEffect
  };

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  /** Municipios con { name, dane } para Colombia */
  const [municipalities, setMunicipalities] = useState([]);
  const [billingMunicipalities, setBillingMunicipalities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingBillingMunicipalities, setLoadingBillingMunicipalities] = useState(false);
  const [shippingCalculating, setShippingCalculating] = useState(false);
  const [errors, setErrors] = useState({});
  const [paymentError, setPaymentError] = useState('');
  const [blockingModal, setBlockingModal] = useState(null);
  // Wompi (API)
  const [wompiPublicKey, setWompiPublicKey] = useState('');
  const [wompiEnv, setWompiEnv] = useState('sandbox');
  const [wompiMerchant, setWompiMerchant] = useState(null);
  const [acceptedWompiPolicy, setAcceptedWompiPolicy] = useState(false);
  const [acceptedWompiPersonalData, setAcceptedWompiPersonalData] = useState(false);

  // Mantener flags existentes para no romper UI (ya no usamos SDK MP)
  const [mercadoPagoInstance] = useState(null);
  const [cardFormInstance, setCardFormInstance] = useState(null);
  const [mpLoading, setMpLoading] = useState(false);
  const [completedOrder, setCompletedOrder] = useState(null);
  /** 'summary' = tarjeta tipo Laika; 'detail' = layout dos columnas */
  const [orderConfirmationView, setOrderConfirmationView] = useState('summary');
  const [orderDetailLoading, setOrderDetailLoading] = useState(false);
  const [orderDetailLoadError, setOrderDetailLoadError] = useState(false);
  const [selectedInstallments, setSelectedInstallments] = useState(1);
  const [availableInstallments, setAvailableInstallments] = useState([]);
  const [loadingInstallments, setLoadingInstallments] = useState(false);
  const [, setIsCreatingForm] = useState(false); // Prevenir múltiples creaciones simultáneas
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [pseBanks, setPseBanks] = useState([]);
  const [pseBanksLoading, setPseBanksLoading] = useState(false);
  const [pseBanksError, setPseBanksError] = useState(null);

  /** Tras iniciar cobro Nequi: esperamos aprobación en app / webhook antes de pantalla confirmada */
  const [nequiAwaiting, setNequiAwaiting] = useState(null);
  const [nequiPolling, setNequiPolling] = useState(false);
  const [nequiAwaitMessage, setNequiAwaitMessage] = useState('');
  /** Orden creada pero cobro aún no confirmado (tarjeta fallida, banco cancelado, etc.) */
  const [pendingPaymentOrder, setPendingPaymentOrder] = useState(null);
  const [paymentMethodChanging, setPaymentMethodChanging] = useState(false);

  const checkoutPseReturnDetect = computeCheckoutPseReturnDetect();

  const [pseReturnState, setPseReturnState] = useState(() =>
    computeCheckoutPseReturnDetect() ? { phase: 'loading' } : null
  );
  const pseReturnFetchedRef = useRef(false);

  /** Totales de pago antes de efectos que dependen de ellos (evita ESLint y TDZ por orden de código). */
  const calculatedSubtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const baseShipping = shipping > 0 ? shipping : 0;
  const couponDiscount = calculateCouponDiscount(appliedCoupon, calculatedSubtotal, baseShipping);
  const couponType = appliedCoupon
    ? String(appliedCoupon.discountType || '').toLowerCase()
    : '';
  const effectiveShipping =
    couponType === 'shipping_free'
      ? Math.max(0, baseShipping - couponDiscount)
      : baseShipping;
  const promoFreeShipping =
    effectiveShipping === 0 &&
    (shippingInfo?.isFreeShipping === true ||
      shippingInfo?.isFreeShippingZone === true ||
      (appliedCoupon &&
        String(appliedCoupon.discountType || '').toLowerCase() === 'shipping_free' &&
        baseShipping > 0));
  const paymentTotal = Math.max(0, calculatedSubtotal + baseShipping - couponDiscount);

  useEffect(() => {
    if (currentStep !== 3 || nequiAwaiting) return;
    const stored = readPendingCheckoutOrder();
    const emailNorm = String(formData.email || '').trim().toLowerCase();
    if (
      stored?.orderId &&
      emailNorm &&
      String(stored.email || '').trim().toLowerCase() === emailNorm &&
      !pendingPaymentOrder
    ) {
      setPendingPaymentOrder({
        orderId: stored.orderId,
        orderNumber: stored.orderNumber || null,
        paymentMethod: null,
        total: null
      });
    }
  }, [currentStep, formData.email, nequiAwaiting, pendingPaymentOrder]);

  useEffect(() => {
    if (formData.wompiPaymentMethod !== 'wompi_nequi') {
      setNequiAwaiting(null);
      setNequiAwaitMessage('');
    }
  }, [formData.wompiPaymentMethod]);

  useEffect(() => {
    if (currentStep === 4) {
      setOrderConfirmationView('summary');
    }
  }, [currentStep]);

  // Diagnóstico: parámetros PSE después de sanear query (evita logs con env=undefined de Wompi)
  useEffect(() => {
    if (!computeCheckoutPseReturnDetect()) return;
    const rawSearch = typeof window !== 'undefined' ? window.location.search || '' : '';
    const cleaned = sanitizePseReturnQueryString(rawSearch);
    console.log(
      'PSE return URL params:',
      cleaned === '?' ? '(sin params útiles tras sanear)' : cleaned
    );
    if (typeof window !== 'undefined') {
      console.log(
        'PSE return path:',
        `${window.location.origin}${window.location.pathname.replace(/\/$/, '') || ''}${cleaned === '?' ? '' : cleaned}`
      );
    }
  }, []);

  // Retorno PSE: fetch solo con id/ref/order_id en URL o en sessionStorage; en /checkout/pse-return?pse=1 sin datos → mensaje
  useEffect(() => {
    if (!checkoutPseReturnDetect || pseReturnFetchedRef.current) {
      return;
    }

    if (typeof window !== 'undefined') {
      const rawSearch = window.location.search || '';
      const cleanedQs = sanitizePseReturnQueryString(rawSearch);
      const shouldRewrite =
        rawSearch !== '' && cleanedQs !== rawSearch;
      if (shouldRewrite) {
        const path = window.location.pathname.replace(/\/$/, '') || '/';
        const suffix = cleanedQs === '?' ? '' : cleanedQs;
        window.history.replaceState({}, '', path + suffix);
      }
    }

    const resolved = resolveSearchForPseReturnStatus(
      typeof window !== 'undefined' ? window.location.search : '',
      typeof window !== 'undefined'
        ? window.location.pathname.replace(/\/$/, '') || '/'
        : '/'
    );
    if (!resolved.shouldFetch) {
      const raw = typeof window !== 'undefined'
        ? window.location.pathname.replace(/\/$/, '') || '/'
        : '';
      const search = typeof window !== 'undefined' ? window.location.search || '' : '';
      if (raw === '/checkout/pse-return' && hasPseQueryMarkerInSearch(search)) {
        pseReturnFetchedRef.current = true;
        setPseReturnState({
          phase: 'loaded',
          outcome: 'error',
          message:
            'Volviste del banco pero la dirección no incluye el id del pago. ' +
            'Si ya pagaste, revisa tu correo (confirmación de Javiland). ' +
            'Para la próxima compra PSE, completa el pago en la misma ventana y no borres el almacenamiento del sitio antes de volver.'
        });
      }
      return;
    }

    pseReturnFetchedRef.current = true;
    const { searchStr, usedStorageFallback } = resolved;

    let cancelled = false;
    (async () => {
      setPseReturnState({ phase: 'loading' });
      try {
        const raw = await paymentService.getPseReturnStatus(searchStr);
        if (cancelled) return;
        if (raw && raw.skipped === true) {
          return;
        }
        if (usedStorageFallback) {
          clearPseReturnBackupStores();
        }
        const inner = unwrapSendSuccessPayload(raw);
        if (!inner || !inner.order) {
          setPseReturnState({
            phase: 'loaded',
            outcome: 'error',
            message: 'No pudimos leer el estado del pago. Si ya pagaste, revisa tu correo o contáctanos.'
          });
          window.history.replaceState({}, '', '/checkout');
          return;
        }

        const outcome = classifyPseBrowserReturn(inner);
        const o = inner.order;
        const amount = o.total != null ? Number(o.total) : 0;

        window.history.replaceState({}, '', '/checkout');

        if (outcome === 'approved') {
          const wompiPm =
            typeof o.wompi_payment_method === 'string' && String(o.wompi_payment_method).trim() !== ''
              ? String(o.wompi_payment_method).trim()
              : 'wompi_pse';
          setPseReturnState(null);

          let merged = null;
          if (!cancelled) {
            merged = await fetchCompletedOrderFromApi(
              o.id,
              {
                paymentMethodKey: wompiPm,
                paymentMethodLabel: getPaymentMethodLabel(wompiPm),
                paymentStatus: 'paid',
                total: amount,
                orderId: o.id,
                orderNumber: o.order_number,
                confirmedAt: o.updated_at || new Date().toISOString()
              },
              { maxAttempts: 3, delayMs: 700 }
            );
          }

          if (!merged) {
            merged = {
              orderId: o.id,
              orderNumber: o.order_number,
              total: amount,
              paymentStatus: 'paid',
              paymentStatusDetail: null,
              paymentMethodKey: wompiPm,
              paymentMethodLabel: getPaymentMethodLabel(wompiPm),
              itemsSnapshot: [],
              subtotal: amount,
              shipping: 0,
              taxAmount: 0,
              shippingRecipientName: '',
              shippingIdentification: '',
              shippingCity: '',
              shippingDepartment: '',
              shippingCountry: '',
              shippingAddressLine: '—',
              shippingZip: '',
              shippingExtra: '',
              customerEmail: '',
              confirmedAt: o.updated_at || new Date().toISOString()
            };
          }

          setCompletedOrder(merged);
          clearPendingCheckoutOrder();
          setCurrentStep(4);
        } else if (outcome === 'rejected') {
          if (o.id) {
            try {
              await orderService.abandonPendingOrder({
                orderId: o.id,
                customerEmail: String(o.customer_email || formData.email || '').trim()
              });
              clearPendingCheckoutOrder();
            } catch (abandonErr) {
              console.error('PSE reject abandon:', abandonErr);
            }
          }
          setPseReturnState({
            phase: 'loaded',
            outcome: 'rejected',
            orderNumber: o.order_number,
            hint: inner?.payment?.status || o.payment_status
          });
        } else {
          setPseReturnState({
            phase: 'loaded',
            outcome: 'pending',
            orderNumber: o.order_number,
            hint: inner?.payment?.status || o.payment_status
          });
        }
      } catch (e) {
        if (cancelled) return;
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          'No pudimos confirmar el estado del pago PSE.';
        setPseReturnState({ phase: 'loaded', outcome: 'error', message: String(msg) });
        window.history.replaceState({}, '', '/checkout');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [checkoutPseReturnDetect]);

  // Cargar departamentos de Colombia
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const response = await shippingService.getDepartments();
        // console.log('Respuesta de departamentos:', response); // Comentado para evitar errores de renderizado
        if (response && response.success) {
          // Normalizar: si son objetos, extraer el nombre; si son strings, usarlos directamente
          const normalizedDepartments = ((response.data && response.data.departments) || response.departments || [])
            .filter(dept => dept != null) // Filtrar valores nulos
            .map(dept => {
              if (typeof dept === 'string') {
                return dept;
              } else if (dept && typeof dept === 'object') {
                // Intentar extraer el nombre de diferentes formas
                return dept.name || dept.department || dept.code || String(dept);
              } else {
                return String(dept);
              }
            })
            .filter(dept => dept && typeof dept === 'string' && dept.trim() !== ''); // Filtrar strings vacíos y asegurar que sean strings
          // console.log('Departamentos normalizados:', normalizedDepartments); // Comentado para evitar errores
          // Validación final: asegurar que todos sean strings
          const finalDepartments = normalizedDepartments.map(d => {
            if (typeof d !== 'string') {
              console.warn('Departamento no es string:', d);
              return String(d);
            }
            return d;
          });
          setDepartments(finalDepartments);
        } else {
          console.error('Error: respuesta sin success o sin departamentos');
          // Fallback: usar lista hardcodeada si el API falla
          setDepartments([
            'Bogotá D.C.',
            'Antioquia',
            'Valle del Cauca',
            'Atlántico',
            'Cundinamarca',
            'Santander',
            'Norte de Santander',
            'Bolívar',
            'Córdoba',
            'Sucre',
            'Magdalena',
            'Cesar',
            'La Guajira',
            'Meta',
            'Casanare',
            'Arauca',
            'Boyacá',
            'Caldas',
            'Risaralda',
            'Quindío',
            'Tolima',
            'Huila',
            'Cauca',
            'Nariño',
            'Putumayo',
            'Amazonas',
            'Caquetá',
            'Guaviare',
            'Vaupés',
            'Guainía',
            'Vichada',
            'San Andrés y Providencia'
          ]);
        }
      } catch (error) {
        console.error('Error cargando departamentos:', error);
        // Fallback: usar lista hardcodeada si hay error
        setDepartments([
          'Bogotá D.C.',
          'Antioquia',
          'Valle del Cauca',
          'Atlántico',
          'Cundinamarca',
          'Santander',
          'Norte de Santander',
          'Bolívar',
          'Córdoba',
          'Sucre',
          'Magdalena',
          'Cesar',
          'La Guajira',
          'Meta',
          'Casanare',
          'Arauca',
          'Boyacá',
          'Caldas',
          'Risaralda',
          'Quindío',
          'Tolima',
          'Huila',
          'Cauca',
          'Nariño',
          'Putumayo',
          'Amazonas',
          'Caquetá',
          'Guaviare',
          'Vaupés',
          'Guainía',
          'Vichada',
          'San Andrés y Providencia'
        ]);
      }
    };
    
    // Cargar departamentos al montar si el país es Colombia
    if (formData.country === 'Colombia') {
      loadDepartments();
    }
  }, [formData.country]);

  // Cargar municipios (DANE) cuando cambie el departamento — Colombia
  useEffect(() => {
    const fetchMunicipalities = async () => {
      if (!formData.department) {
        setMunicipalities([]);
        setFormData((prev) => ({ ...prev, city: '', municipalityDane: '' }));
        return;
      }

      setFormData((prev) => ({ ...prev, city: '', municipalityDane: '' }));
      setLoadingCities(true);
      try {
        const response = await shippingService.getMunicipalitiesByDepartment(formData.department);
        const list =
          (response && response.data && response.data.municipalities) ||
          (response && response.municipalities) ||
          [];
        setMunicipalities(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error('Error cargando municipios:', error);
        setMunicipalities([]);
      } finally {
        setLoadingCities(false);
      }
    };

    if (formData.country === 'Colombia') {
      fetchMunicipalities();
    }
  }, [formData.department, formData.country]);

  // Municipios para dirección de facturación (misma lógica DANE)
  useEffect(() => {
    if (formData.country !== 'Colombia' || !formData.useDifferentBillingAddress) {
      setBillingMunicipalities([]);
      return;
    }
    const fetchBillingMun = async () => {
      if (!formData.billingDepartment) {
        setBillingMunicipalities([]);
        setFormData((prev) => ({ ...prev, billingCity: '', billingMunicipalityDane: '' }));
        return;
      }
      setFormData((prev) => ({ ...prev, billingCity: '', billingMunicipalityDane: '' }));
      setLoadingBillingMunicipalities(true);
      try {
        const response = await shippingService.getMunicipalitiesByDepartment(formData.billingDepartment);
        const list =
          (response && response.data && response.data.municipalities) ||
          (response && response.municipalities) ||
          [];
        setBillingMunicipalities(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error('Error cargando municipios (facturación):', e);
        setBillingMunicipalities([]);
      } finally {
        setLoadingBillingMunicipalities(false);
      }
    };
    fetchBillingMun();
  }, [formData.billingDepartment, formData.country, formData.useDifferentBillingAddress]);


  // Paso tarjeta: NO usar mercadoPago.cardForm() — el SDK CDN llama a GET /v1/payment_methods/search,
  // que hoy responde 404 en api.mercadopago.com (rompe preload y el checkout). Flujo: campos propios
  // + createCardToken() + cuotas vía backend (/payments/installments).
  useEffect(() => {
    if (!mercadoPagoInstance ||
        currentStep !== 3 ||
        !isWompiCard(formData.wompiPaymentMethod)) {
      if (cardFormInstance) {
        try {
          cardFormInstance.destroy();
        } catch (e) {
          /* noop */
        }
        setCardFormInstance(null);
      }
      if (!isWompiCard(formData.wompiPaymentMethod)) {
        setPaymentError('');
      }
      setMpLoading(false);
      setIsCreatingForm(false);
      return;
    }

    if (cardFormInstance) {
      try {
        cardFormInstance.destroy();
      } catch (e) {
        /* noop */
      }
      setCardFormInstance(null);
    }

    if (availableInstallments.length === 0 && paymentTotal > 0) {
      setAvailableInstallments([
        {
          installments: 1,
          installment_rate: 0,
          total_amount: paymentTotal,
          installment_amount: paymentTotal
        }
      ]);
    }

    setIsCreatingForm(false);
    setMpLoading(false);
    setPaymentError('');
    console.log('ℹ️ Checkout tarjeta: modo sin cardForm (evita 404 payment_methods/search del SDK). Usar createCardToken.');

    return () => {};
  }, [
    mercadoPagoInstance,
    cardFormInstance,
    currentStep,
    formData.wompiPaymentMethod,
    paymentTotal,
    availableInstallments
  ]);

  // Actualizar datos dinámicos del formulario (monto y email) cuando cambien
  useEffect(() => {
    if (cardFormInstance) {
      try {
        cardFormInstance.update({
          amount: String(total || 0),
          cardholderEmail: formData.email || ''
        });
      } catch (error) {
        console.warn('No se pudo actualizar el formulario de tarjeta', error);
      }
    }
  }, [cardFormInstance, total, formData.email]);

  // Inicializar cuotas por defecto cuando se monta el formulario o cambia el total
  useEffect(() => {
    if (currentStep === 3 && isWompiCard(formData.wompiPaymentMethod)) {
      if (availableInstallments.length === 0 && paymentTotal > 0) {
        setAvailableInstallments([
          {
            installments: 1,
            installment_rate: 0,
            total_amount: paymentTotal,
            installment_amount: paymentTotal
          }
        ]);
        setSelectedInstallments(1);
      }
    }
  }, [currentStep, formData.wompiPaymentMethod, paymentTotal, availableInstallments, items]);

  // Debug: Verificar estado del botón de completar compra
  useEffect(() => {
    if (currentStep === 3 && isWompiCard(formData.wompiPaymentMethod)) {
      console.log('🔍 Estado del botón Completar Compra:', {
        loading,
        mpLoading,
        mercadoPagoInstance: !!mercadoPagoInstance,
        wompiPaymentMethod: formData.wompiPaymentMethod,
        disabled: loading || (isWompiCard(formData.wompiPaymentMethod) && mpLoading)
      });
    }
  }, [currentStep, formData.wompiPaymentMethod, loading, mpLoading, mercadoPagoInstance]);

  // Nota: Los campos de identificación ahora están en el checkout (paso 3, sección de facturación)
  // Ya no necesitamos poblar los campos de MercadoPago porque usamos los campos del checkout

  // Escuchar cambios en el campo de número de tarjeta como fallback
  useEffect(() => {
    if (currentStep !== 3) return;
    if (!isWompiCard(formData.wompiPaymentMethod)) return;

    const cardNumberInput = document.getElementById('form-card-number');
    if (!cardNumberInput) return;

    let debounceTimeout = null;

    const handleCardNumberChange = async () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }

      debounceTimeout = setTimeout(async () => {
        try {
          const raw = cardNumberInput.value.replace(/\s/g, '');
          if (raw.length < 6 || paymentTotal <= 0) {
            return;
          }
          const bin = raw.substring(0, 6);
          const paymentMethodId = detectPaymentMethodIdFromPan(raw);
          setLoadingInstallments(true);
          console.log('📞 Consultando cuotas (BIN local):', { paymentMethodId, bin, correctTotal: paymentTotal });

          const result = await paymentService.getInstallments(paymentMethodId, bin, paymentTotal);
          console.log('✅ Respuesta de cuotas:', result);

          if (result.success && result.installments && result.installments.length > 0) {
            setAvailableInstallments(result.installments);
            const firstInstallment = result.installments[0].installments;
            if (selectedInstallments > firstInstallment || !result.installments.find(i => i.installments === selectedInstallments)) {
              setSelectedInstallments(firstInstallment);
            }
          } else {
            setAvailableInstallments([
              {
                installments: 1,
                installment_rate: 0,
                total_amount: paymentTotal,
                installment_amount: paymentTotal
              }
            ]);
            setSelectedInstallments(1);
          }
        } catch (error) {
          console.error('❌ Error obteniendo cuotas:', error);
          setAvailableInstallments([
            {
              installments: 1,
              installment_rate: 0,
              total_amount: paymentTotal,
              installment_amount: paymentTotal
            }
          ]);
          setSelectedInstallments(1);
        } finally {
          setLoadingInstallments(false);
        }
      }, 600);
    };

    cardNumberInput.addEventListener('input', handleCardNumberChange);
    cardNumberInput.addEventListener('change', handleCardNumberChange);

    return () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
      cardNumberInput.removeEventListener('input', handleCardNumberChange);
      cardNumberInput.removeEventListener('change', handleCardNumberChange);
    };
  }, [currentStep, paymentTotal, selectedInstallments, formData.wompiPaymentMethod, items]);

  // Redirigir si el carrito está vacío y calcular subtotal inicial
  // No redirigir si estamos en el paso 4 (confirmación)
  useEffect(() => {
    if (items.length === 0 && currentStep !== 4 && pseReturnState == null) {
      navigateToPage('/tienda');
      return;
    }
    
    // Calcular subtotal directamente desde los items si no está actualizado
    const calculatedSubtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    if (subtotal === 0 && calculatedSubtotal > 0) {
      // Actualizar cálculos sin dirección de envío para inicializar subtotal
      updateCalculations(null).catch(err => {
        console.error('Error actualizando cálculos iniciales:', err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, currentStep, pseReturnState]);


  // Calcular envío automáticamente cuando se completa el formulario de envío
  useEffect(() => {
    if (currentStep !== 2) return;
    const col = formData.country === 'Colombia';
    const billingPricing =
      col && formData.useDifferentBillingAddress;

    if (billingPricing) {
      if (!formData.billingAddress?.trim() || !formData.billingDepartment) return;
      if (!formData.billingMunicipalityDane || !formData.billingCity) return;
    } else {
      if (!formData.department || !formData.address?.trim()) return;
      if (col) {
        if (!formData.municipalityDane || !formData.city) return;
      } else if (!formData.city) return;
    }

    const timer = setTimeout(() => {
      handleShippingRecalculation();
    }, 500);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- debounce; handleShipping declarado después
  }, [
    currentStep,
    formData.city,
    formData.department,
    formData.address,
    formData.municipalityDane,
    formData.country,
    formData.useDifferentBillingAddress,
    formData.billingAddress,
    formData.billingCity,
    formData.billingDepartment,
    formData.billingMunicipalityDane
  ]);

  // Inicializar Wompi (API)
  useEffect(() => {
    const initializeWompi = async () => {
      try {
        const response = await paymentService.getPublicKey();
        const publicKey = response?.data?.publicKey || '';
        const env = response?.data?.env || 'sandbox';

        if (!publicKey) {
          setPaymentError('Wompi no está configurado (public key). Revisa variables de entorno del backend.');
          return;
        }

        setWompiPublicKey(publicKey);
        setWompiEnv(env);

        // Obtener acceptance tokens + permalinks (contratos) para mostrar al usuario y enviar con la transacción.
        try {
          const merchantResp = await paymentService.getMerchant();
          const merchant = merchantResp?.data?.merchant || null;
          setWompiMerchant(merchant);
        } catch (e) {
          console.error('Error obteniendo merchant Wompi:', e);
          setPaymentError('No se pudo obtener la información de Wompi (contratos/acceptance tokens).');
        }
      } catch (error) {
        console.error('Error inicializando Wompi:', error);
        setPaymentError('No se pudo inicializar Wompi. Revisa /api/payments/mode y /api/payments/public-key.');
      }
    };

    initializeWompi();
  }, []);

  useEffect(() => {
    if (currentStep !== 3 || formData.wompiPaymentMethod !== 'wompi_pse') return;
    let cancelled = false;
    (async () => {
      setPseBanksLoading(true);
      setPseBanksError(null);
      try {
        const resp = await paymentService.getPseFinancialInstitutions();
        const arr = normalizePseFinancialInstitutionsResponse(resp).filter((b) => {
          const code =
            b.code != null
              ? String(b.code).trim()
              : b.financial_institution_code != null
                ? String(b.financial_institution_code).trim()
                : '';
          return code !== '';
        });
        if (!cancelled) {
          setPseBanks(arr);
          if (arr.length === 0) {
            setPseBanksError('No pudimos cargar los bancos. Intenta nuevamente.');
          }
        }
      } catch (e) {
        console.warn('No se cargaron bancos PSE:', e);
        if (!cancelled) {
          setPseBanks([]);
          setPseBanksError('No pudimos cargar los bancos. Intenta nuevamente.');
        }
      } finally {
        if (!cancelled) setPseBanksLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentStep, formData.wompiPaymentMethod]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Limpiar error del campo si existe
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Si cambia el método de pago, limpiar errores de pago
    if (name === 'wompiPaymentMethod') {
      setPaymentError('');
    }
  };

  const handleShippingRecalculation = async () => {
    const col = formData.country === 'Colombia';
    const useBillingForPricing = col && formData.useDifferentBillingAddress;

    if (useBillingForPricing) {
      if (
        !formData.billingAddress?.trim() ||
        !formData.billingDepartment ||
        !formData.billingMunicipalityDane ||
        !formData.billingCity
      ) {
        return;
      }
    } else if (col) {
      if (!formData.city || !formData.department || !formData.municipalityDane) return;
    } else {
      if (!formData.city || !formData.state) return;
    }

    setShippingCalculating(true);
    try {
      const addressData = useBillingForPricing
        ? {
            address: formData.billingAddress,
            fullAddress: formData.billingAddress,
            city: formData.billingCity,
            municipalityDane: formData.billingMunicipalityDane || '',
            department: formData.billingDepartment || '',
            state: formData.state || '',
            zipCode: formData.zipCode || '',
            country: formData.country || 'Colombia',
            shippingAdditionalInfo: formData.shippingAdditionalInfo || '',
            validatedAddress: formData.validatedAddress || null
          }
        : {
            address: formData.address,
            fullAddress: formData.address,
            city: formData.city,
            municipalityDane: formData.municipalityDane || '',
            department: formData.department || '',
            state: formData.state || '',
            zipCode: formData.zipCode || '',
            country: formData.country || 'Colombia',
            shippingAdditionalInfo: formData.shippingAdditionalInfo || '',
            validatedAddress: formData.validatedAddress || null
          };

      setShippingAddress(addressData);

      await updateCalculations(addressData);
      
    } catch (error) {
      console.error('Error recalculando envío:', error);
    } finally {
      setShippingCalculating(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(price || 0);
  };

  /** Tras Nequi cancelado/rechazado: libera inventario y cancela la orden (mismo email que el checkout). */
  const showBlockingCheckoutError = (message, title = 'No se puede completar el pago') => {
    const msg = String(message || '').trim();
    if (!msg) return;
    setPaymentError(msg);
    setBlockingModal({ title, message: msg });
  };

  const dismissBlockingModal = () => {
    setBlockingModal(null);
  };

  const abandonGuestCheckoutOrder = async (orderId) => {
    const email = String(formData.email || '').trim();
    if (!orderId || !email) return false;
    try {
      await orderService.abandonPendingOrder({ orderId, customerEmail: email });
      clearPendingCheckoutOrder();
      return true;
    } catch (e) {
      console.error('abandonGuestCheckoutOrder:', e);
      const m = e?.response?.data?.message;
      setPaymentError(
        typeof m === 'string' && m.trim() !== ''
          ? m
          : 'No se pudo liberar el pedido anterior. Espera unos segundos o recarga la página.'
      );
      return false;
    }
  };

  /** Tras iniciar cobro Nequi: consultar si el servidor ya marca la orden pagada (webhook Wompi / sync). */
  const handleNequiPaidCheckClick = async () => {
    if (!nequiAwaiting?.orderId) return;
    setNequiPolling(true);
    setPaymentError('');
    try {
      const raw = await paymentService.getPaymentStatus(nequiAwaiting.orderId);
      const inner = unwrapSendSuccessPayload(raw);
      if (!inner?.order) {
        setPaymentError('No pudimos consultar el estado. Intenta de nuevo.');
        return;
      }
      const outcome = classifyPseBrowserReturn(inner);
      const o = inner.order;
      if (outcome === 'approved') {
        const amount = o.total != null ? Number(o.total) : Number(nequiAwaiting.total || 0);
        const wmKey =
          typeof o.wompi_payment_method === 'string' && o.wompi_payment_method.trim() !== ''
            ? o.wompi_payment_method.trim()
            : 'wompi_nequi';

        const oid = o.id ?? nequiAwaiting.orderId;
        let mergedNequi = await fetchCompletedOrderFromApi(
          oid,
          {
            paymentMethodKey: wmKey,
            paymentStatus: 'paid',
            paymentStatusDetail: null,
            total: amount,
            orderId: oid,
            orderNumber: o.order_number ?? nequiAwaiting.orderNumber,
            confirmedAt: o.updated_at || new Date().toISOString()
          },
          { maxAttempts: 3, delayMs: 700 }
        );

        if (!mergedNequi) {
          const snap = (nequiAwaiting.itemsSnapshot || []).map((line) => ({
            ...line,
            image: normalizeOrderLineImage(line.image)
          }));
          mergedNequi = {
            orderId: o.id ?? nequiAwaiting.orderId,
            orderNumber: o.order_number ?? nequiAwaiting.orderNumber,
            total: amount,
            paymentStatus: 'paid',
            paymentStatusDetail: null,
            paymentMethodKey: wmKey,
            paymentMethodLabel: getPaymentMethodLabel(wmKey),
            itemsSnapshot: snap,
            subtotal: calculatedSubtotal,
            shipping: effectiveShipping,
            taxAmount: tax || 0,
            ...buildShippingSnapshotFromCheckoutForm(formData),
            customerEmail: formData.email,
            confirmedAt: o.updated_at || new Date().toISOString()
          };
        }

        setCompletedOrder(mergedNequi);
        setNequiAwaiting(null);
        setNequiAwaitMessage('');
        clearPendingCheckoutOrder();
        setCurrentStep(4);
        setTimeout(() => {
          clearCart();
        }, 100);
        return;
      }
      if (outcome === 'rejected') {
        const released = await abandonGuestCheckoutOrder(nequiAwaiting.orderId);
        setPaymentError(
          released
            ? 'El pago fue rechazado o cancelado en Nequi. Ya liberamos tu reserva de inventario; puedes elegir otro método de pago y volver a confirmar.'
            : 'El pago fue rechazado o cancelado. Revisa el mensaje de arriba; si persiste, espera un momento o recarga la página.'
        );
        setNequiAwaitMessage('');
        setNequiAwaiting(null);
        return;
      }
      setNequiAwaitMessage(
        'Aún no vemos tu pago aprobado. Abre Nequi y acepta la solicitud; luego pulsa «Ya pagué» de nuevo.'
      );
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        'No pudimos consultar el estado del pago.';
      setPaymentError(String(msg));
    } finally {
      setNequiPolling(false);
    }
  };

  const resolvePendingCheckoutOrderId = () => {
    const stored = readPendingCheckoutOrder();
    return (
      pendingPaymentOrder?.orderId ??
      nequiAwaiting?.orderId ??
      stored?.orderId ??
      null
    );
  };

  const resolvePendingCheckoutOrderNumber = () => {
    const stored = readPendingCheckoutOrder();
    return (
      pendingPaymentOrder?.orderNumber ??
      nequiAwaiting?.reference ??
      nequiAwaiting?.orderNumber ??
      stored?.orderNumber ??
      null
    );
  };

  /**
   * Cancela el pedido pendiente, devuelve inventario y permite elegir otro medio de pago.
   */
  const handleChangePaymentMethod = async ({ source = 'general' } = {}) => {
    const orderId = resolvePendingCheckoutOrderId();
    if (!orderId) return false;

    const confirmMsg =
      source === 'nequi'
        ? '¿Abandonar el pago por Nequi? El pedido se cancelará y el inventario quedará libre para que puedas elegir otro método (se creará un pedido nuevo al confirmar).'
        : '¿Cambiar método de pago? Se cancelará el pedido pendiente, liberaremos el inventario y podrás elegir otro medio (al confirmar se creará un pedido nuevo).';

    if (!window.confirm(confirmMsg)) {
      return false;
    }

    setPaymentMethodChanging(true);
    setPaymentError('');
    setBlockingModal(null);
    try {
      const ok = await abandonGuestCheckoutOrder(orderId);
      if (ok) {
        setPendingPaymentOrder(null);
        setNequiAwaiting(null);
        setNequiAwaitMessage('');
        setPaymentError('');
        setBlockingModal(null);
      }
      return ok;
    } finally {
      setPaymentMethodChanging(false);
    }
  };

  const handleNequiChangePaymentMethod = () => handleChangePaymentMethod({ source: 'nequi' });

  const getStepErrors = (step) => {
    const newErrors = {};

    switch (step) {
      case 1: // Información Personal
        if (!formData.firstName.trim()) newErrors.firstName = 'El nombre es requerido';
        if (!formData.lastName.trim()) newErrors.lastName = 'El apellido es requerido';
        if (!formData.email.trim()) newErrors.email = 'El email es requerido';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email inválido';
        if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'El número de teléfono es requerido';
        else if (!/^\d{7,10}$/.test(formData.phoneNumber.replace(/\s/g, ''))) {
          newErrors.phoneNumber = 'El número de teléfono debe tener entre 7 y 10 dígitos';
        }
        if (!formData.identificationType) {
          newErrors.identificationType = 'Selecciona el tipo de documento.';
        } else {
          const personalIdErr = validateIdentificationFormat(formData.identificationType, formData.identificationNumber, 'personal');
          if (personalIdErr) {
            newErrors.identificationNumber = personalIdErr;
          }
        }
        break;

      case 2: // Dirección de Envío
        if (!formData.address.trim()) {
          newErrors.address = 'La dirección es requerida';
        }
        if (!formData.department) {
          newErrors.department = 'El departamento es requerido';
        }
        if (!formData.city) {
          newErrors.city = 'El municipio es requerido';
        }
        if (formData.country === 'Colombia' && !formData.municipalityDane) {
          newErrors.municipalityDane = 'Selecciona un municipio de la lista';
        }
        if (formData.useDifferentBillingAddress) {
          if (!formData.billingInvoiceFullName?.trim()) {
            newErrors.billingInvoiceFullName = 'Nombre o razón social del titular de la factura (requerido)';
          }
          if (!formData.billingIdentificationType) {
            newErrors.billingIdentificationType = 'Tipo de documento del titular de la factura es requerido';
          } else {
            const billIdErr = validateIdentificationFormat(
              formData.billingIdentificationType,
              formData.billingIdentificationNumber,
              'billing'
            );
            if (billIdErr) {
              newErrors.billingIdentificationNumber = billIdErr;
            }
          }
          if (!formData.billingAddress.trim()) {
            newErrors.billingAddress = 'La dirección es requerida';
          }
          if (!formData.billingDepartment) {
            newErrors.billingDepartment = 'El departamento es requerido';
          }
          if (!formData.billingCity) {
            newErrors.billingCity = 'El municipio es requerido';
          }
          if (formData.country === 'Colombia' && !formData.billingMunicipalityDane) {
            newErrors.billingMunicipalityDane = 'Selecciona un municipio de la lista';
          }
        }
        break;

      case 3: // Pago y Facturación — instrumento Wompi (tarjeta vs PSE / Nequi / Botón BC)
        {
          const wm = formData.wompiPaymentMethod;

          const requireWompiContracts =
            wm === 'wompi_pse' ||
            wm === 'wompi_nequi' ||
            wm === 'wompi_bancolombia_transfer' ||
            isWompiCard(wm);
          if (requireWompiContracts) {
            if (!acceptedWompiPolicy) {
              newErrors.acceptWompiTerms = 'Debes aceptar los términos de Wompi.';
            }
            if (!acceptedWompiPersonalData) {
              newErrors.acceptWompiData = 'Debes autorizar el tratamiento de datos personales en Wompi.';
            }
          }

          if (wm === 'wompi_pse') {
            if (!String(formData.pseFinancialInstitutionCode || '').trim()) {
              newErrors.pseFinancialInstitutionCode = 'Selecciona tu banco para pagar con PSE.';
            }
          }
          if (wm === 'wompi_nequi') {
            const n10 = normalizeNequiPhone10Digits(formData.nequiPhone);
            if (!isNequiPhone10Valid(n10)) {
              newErrors.nequiPhone =
                'Ingresa tu celular Nequi: 10 dígitos, sin +57 ni espacios (ej. 3001234567).';
            }
          }
        }
        break;

      default:
        break;
    }

    return newErrors;
  };

  const validateStep = (step) => {
    const newErrors = getStepErrors(step);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateAllStepsForSubmit = () => {
    const merged = {
      ...getStepErrors(1),
      ...getStepErrors(2),
      ...getStepErrors(3)
    };
    setErrors(merged);
    return merged;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
      if (currentStep >= 2) {
        setPaymentError('');
      }
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  /** Volver a un paso anterior ya completado (indicador clickeable). */
  const goToStep = (target) => {
    if (target < 1 || target > 3) return;
    if (currentStep <= target) return;
    setCurrentStep(target);
    setPaymentError('');
  };

  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) {
      setCouponError('Ingresa un código de cupón.');
      return;
    }

    try {
      setCouponLoading(true);
      setCouponError('');
      const resp = await couponService.validateCoupon(code, { useMemberAuth: true });
      const coupon = resp?.data?.coupon || resp?.coupon;
      if (!resp?.success || !coupon) {
        setAppliedCoupon(null);
        setCouponError(resp?.message || 'Cupón no válido.');
        return;
      }

      const minPurchase = Number(coupon.minPurchase || 0);
      if (calculatedSubtotal < minPurchase) {
        setAppliedCoupon(null);
        setCouponError(`Este cupón requiere compra mínima de ${formatPrice(minPurchase)}.`);
        return;
      }

      setAppliedCoupon(coupon);
    } catch (err) {
      setAppliedCoupon(null);
      const msg = err?.response?.data?.message || 'No se pudo validar el cupón.';
      setCouponError(msg);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('🔍 handleSubmit iniciado - Instrumento Wompi:', formData.wompiPaymentMethod);
    console.log('🔍 Validando paso 3...');
    
    const mergedErrors = validateAllStepsForSubmit();
    const isValid = Object.keys(mergedErrors).length === 0;
    console.log('🔍 Resultado de validación:', isValid);
    console.log('Errores:', JSON.stringify(mergedErrors, null, 2));

    if (!isValid) {
      console.error('❌ Validación falló. Errores:', JSON.stringify(mergedErrors, null, 2));
      const step1Keys = [
        'firstName',
        'lastName',
        'email',
        'phoneNumber',
        'identificationType',
        'identificationNumber'
      ];
      const step2Keys = [
        'address',
        'department',
        'city',
        'municipalityDane',
        'billingAddress',
        'billingDepartment',
        'billingCity',
        'billingMunicipalityDane',
        'billingInvoiceFullName',
        'billingIdentificationType',
        'billingIdentificationNumber'
      ];
      if (step1Keys.some((k) => mergedErrors[k])) setCurrentStep(1);
      else if (step2Keys.some((k) => mergedErrors[k])) setCurrentStep(2);
      else setCurrentStep(3);
      setTimeout(() => {
        const firstErrorField = document.querySelector('.checkout-form .error');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (typeof firstErrorField.focus === 'function') firstErrorField.focus();
        }
      }, 0);
      return;
    }
    
    console.log('✅ Validación pasada, continuando con el proceso...');

    if (!wompiPublicKey) {
      setPaymentError('Wompi aún no está listo (public key). Recarga la página o revisa /api/payments/public-key.');
      return;
    }
    if (!wompiMerchant?.presigned_acceptance?.acceptance_token || !wompiMerchant?.presigned_personal_data_auth?.acceptance_token) {
      setPaymentError('Wompi aún no está listo (acceptance tokens). Recarga o revisa /api/payments/merchant.');
      return;
    }

    if (formData.wompiPaymentMethod === 'wompi_nequi') {
      const n10 = normalizeNequiPhone10Digits(formData.nequiPhone);
      if (!isNequiPhone10Valid(n10)) {
        setErrors((prev) => ({
          ...prev,
          nequiPhone:
            'Ingresa tu celular Nequi: 10 dígitos, sin +57 ni espacios (ej. 3001234567).'
        }));
        return;
      }
    }

    // Validaciones adicionales para métodos de tarjeta
    if (isWompiCard(formData.wompiPaymentMethod)) {
      const cardNumberInput = document.getElementById('form-card-number');
      const cardholderEmailInput = document.getElementById('form-cardholder-email');
      const cardholderNameInput = document.getElementById('form-cardholder-name');
      const expirationInput = document.getElementById('form-card-expiration-date');
      const securityCodeInput = document.getElementById('form-card-security-code');

      // Validar nombre en la tarjeta
      if (!cardholderNameInput || !cardholderNameInput.value || cardholderNameInput.value.trim() === '') {
        setPaymentError('Por favor ingresa el nombre como figura en la tarjeta.');
        return;
      }

      // Validar email del titular
      if (!cardholderEmailInput || !cardholderEmailInput.value || cardholderEmailInput.value.trim() === '') {
        setPaymentError('Por favor ingresa el email del titular de la tarjeta.');
        return;
      }

      // Validar formato de email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(cardholderEmailInput.value.trim())) {
        setPaymentError('Por favor ingresa un email válido.');
        return;
      }

      // Validar número de tarjeta
      if (!cardNumberInput || !cardNumberInput.value || cardNumberInput.value.trim() === '') {
        setPaymentError('Por favor ingresa el número de tarjeta.');
        return;
      }

      // Validar formato de número de tarjeta (solo números, mínimo 13, máximo 19 dígitos)
      const cardNumber = cardNumberInput.value.replace(/\s/g, ''); // Remover espacios
      if (cardNumber.length === 0) {
        setPaymentError('Por favor ingresa el número de tarjeta.');
        return;
      }
      if (cardNumber.length < 13 || cardNumber.length > 19) {
        setPaymentError(`El número de tarjeta debe tener entre 13 y 19 dígitos. Actualmente tiene ${cardNumber.length} dígitos.`);
        return;
      }
      if (!/^\d+$/.test(cardNumber)) {
        setPaymentError('El número de tarjeta solo puede contener dígitos.');
        return;
      }

      // Validar fecha de vencimiento
      if (!expirationInput || !expirationInput.value || expirationInput.value.length !== 4) {
        setPaymentError('Por favor ingresa la fecha de vencimiento en formato MMAA (ej: 1130 = noviembre 2030).');
        return;
      }

      // Validar formato de fecha (solo números, 4 dígitos)
      if (!/^\d{4}$/.test(expirationInput.value)) {
        setPaymentError('La fecha de vencimiento debe tener 4 dígitos (MMAA).');
        return;
      }

      // Validar que el mes sea válido (01-12)
      const month = parseInt(expirationInput.value.substring(0, 2));
      if (month < 1 || month > 12) {
        setPaymentError('El mes de vencimiento debe estar entre 01 y 12.');
        return;
      }

      // Validar código de seguridad
      if (!securityCodeInput || !securityCodeInput.value || securityCodeInput.value.length < 3) {
        setPaymentError('Por favor ingresa el código de seguridad (CVV) de 3 o 4 dígitos.');
        return;
      }

      // Validar formato de CVV (solo números, 3 o 4 dígitos)
      if (!/^\d{3,4}$/.test(securityCodeInput.value)) {
        setPaymentError('El código de seguridad debe tener 3 o 4 dígitos.');
        return;
      }
    }

    setLoading(true);
    setPaymentError('');

    /** Evitar setLoading(false) en finally cuando vamos al banco: el finally corre tras return y puede re-renderizar antes de navegar */
    let skipLoadingResetDuringBankRedirect = false;

    try {
      // Solo obtener datos de tarjeta si el método de pago es tarjeta (Wompi API)
      let token = null; // tok_test_... / tok_prod_...
      let identificationType = 'CC';
      let identificationNumber = '0000000000';

      if (isWompiCard(formData.wompiPaymentMethod)) {
        const cardNumberInput = document.getElementById('form-card-number');
        const expirationInput = document.getElementById('form-card-expiration-date');
        const securityCodeInput = document.getElementById('form-card-security-code');
        const cardholderNameInput = document.getElementById('form-cardholder-name');
        const cardholderEmailInput = document.getElementById('form-cardholder-email');

        if (!cardNumberInput || !expirationInput || !securityCodeInput || !cardholderNameInput || !cardholderEmailInput) {
          throw new Error('Faltan campos requeridos para procesar el pago. Verifica que todos los campos estén completos.');
        }

        const number = cardNumberInput.value.replace(/\s/g, '');
        const expiration = expirationInput.value;
        const expMonth = expiration.substring(0, 2);
        const expYear = expiration.substring(2, 4); // Wompi requiere 2 dígitos (YY)
        const cvc = securityCodeInput.value;
        const cardHolder = cardholderNameInput.value.trim();

        identificationType = formData.identificationType || 'CC';
        identificationNumber = formData.identificationNumber.replace(/\s|-/g, '') || '0000000000';

        const wompiBaseUrl = getWompiBaseUrl(wompiEnv);
        const tokenResp = await fetch(`${wompiBaseUrl}/tokens/cards`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${wompiPublicKey}`
          },
          body: JSON.stringify({
            number,
            exp_month: expMonth,
            exp_year: expYear,
            cvc,
            card_holder: cardHolder
          })
        });

        const tokenJson = await tokenResp.json().catch(() => ({}));
        if (!tokenResp.ok || tokenJson?.status !== 'CREATED' || !tokenJson?.data?.id) {
          const msg = tokenJson?.error?.reason || tokenJson?.error?.message || 'Wompi no pudo tokenizar la tarjeta. Revisa los datos e intenta de nuevo.';
          throw new Error(msg);
        }

        token = tokenJson.data.id;

        // El email se usa desde formData.email (step 1). Si está vacío, valida arriba.
      } else {
        // Para PSE y Efectivo (si se mantiene), usar los campos del checkout (facturación)
        identificationType = formData.identificationType || 'CC';
        identificationNumber = formData.identificationNumber.replace(/\s|-/g, '') || '0000000000';
      }

      // Asegurar que los totales estén definidos y calculados correctamente
      const finalSubtotal = calculatedSubtotal || subtotal || 0;
      const finalTax = tax || 0;
      const finalShipping = effectiveShipping;
      const finalTotal = Math.max(0, finalSubtotal + finalTax + finalShipping - (couponDiscount || 0));
      
      console.log('📊 Totales calculados para la orden:', {
        calculatedSubtotal,
        subtotal,
        finalSubtotal,
        tax: finalTax,
        shipping: finalShipping,
        total: finalTotal,
        paymentTotal
      });

      const orderPayload = {
        customer: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: `${formData.phoneCountryCode}${formData.phoneNumber.replace(/\s/g, '')}`,
          identificationNumber: formData.identificationNumber.replace(/\s|-/g, ''),
          identificationType: formData.identificationType
        },
        shippingAddress: {
          address: formData.address,
          city: formData.city,
          municipalityDane: formData.municipalityDane || undefined,
          department: formData.department || '',
          state: formData.state || '',
          zipCode: formData.zipCode || '',
          country: formData.country,
          shippingAdditionalInfo: formData.shippingAdditionalInfo || ''
        },
        billingAddress: formData.useDifferentBillingAddress
          ? {
              address: formData.billingAddress,
              city: formData.billingCity,
              department: formData.billingDepartment,
              municipalityDane: formData.billingMunicipalityDane || undefined,
              recipientName: formData.billingInvoiceFullName.trim(),
              identificationType: formData.billingIdentificationType,
              identificationNumber: formData.billingIdentificationNumber.replace(/\s|-/g, '')
            }
          : null,
        useDifferentBillingAddress: formData.useDifferentBillingAddress,
        shippingInfo,
        items: items.map(item => {
          const productId = item.productId || item.id;
          return {
            id: parseInt(productId, 10), // El validador espera 'id' como número entero
            productId: parseInt(productId, 10), // También enviar productId para compatibilidad
            name: item.name,
            price: parseFloat(item.price) || 0,
            quantity: parseInt(item.quantity, 10) || 1,
            selectedSize: item.selectedSize,
            selectedColor: item.selectedColor
          };
        }),
        totals: {
          subtotal: finalSubtotal,
          tax: finalTax,
          shipping: finalShipping,
          total: finalTotal,
          taxBreakdown: taxBreakdown || {},
          couponCode: appliedCoupon?.code || null,
          couponDiscount: couponDiscount || 0
        },
        paymentMethod: 'wompi',
        wompiPaymentMethod: formData.wompiPaymentMethod,
        customerNotes: formData.customerNotes
      };
      
      console.log('📤 Payload de la orden a enviar:', {
        ...orderPayload,
        totals: orderPayload.totals,
        items: orderPayload.items.length + ' items'
      });

      let orderResponse;
      try {
        const pendingPrev = readPendingCheckoutOrder();
        const emailNorm = String(formData.email || '').trim().toLowerCase();
        if (
          pendingPrev?.orderId &&
          String(pendingPrev.email || '').trim().toLowerCase() === emailNorm
        ) {
          await abandonGuestCheckoutOrder(pendingPrev.orderId);
        }
        orderResponse = await orderService.createOrder(orderPayload);
      } catch (orderErr) {
        console.error('Error registrando orden (POST /orders):', orderErr);
        console.error('Error crear orden:', orderErr?.response?.data || orderErr?.message || orderErr);
        const srvMsg = orderErr?.response?.data?.message;
        const errText =
          typeof srvMsg === 'string' && srvMsg.trim() !== ''
            ? srvMsg
            : 'Error al registrar tu pedido. Por favor intenta nuevamente.';
        if (/stock insuficiente/i.test(errText)) {
          showBlockingCheckoutError(
            errText,
            'Sin stock disponible'
          );
        } else {
          setPaymentError(errText);
        }
        return;
      }

      console.log('📥 Respuesta completa de createOrder:', orderResponse);

      const order = orderResponse?.data?.order || orderResponse?.order;

      if (!orderResponse?.success || !order) {
        setPaymentError(
          orderResponse?.message ||
            'Error al registrar tu pedido. Por favor intenta nuevamente.'
        );
        return;
      }

      console.log('✅ Orden creada exitosamente:', order);
      const wm = formData.wompiPaymentMethod;
      writePendingCheckoutOrder(order.id, formData.email, order.orderNumber);
      setPendingPaymentOrder({
        orderId: order.id,
        orderNumber: order.orderNumber,
        paymentMethod: wm,
        total: paymentTotal
      });

      const paymentPayload = {
        orderId: order.id,
        orderNumber: order.orderNumber,
        amount: Number(paymentTotal),
        payer: {
          email: formData.email,
          identification: {
            type: identificationType,
            number: identificationNumber
          },
          firstName: formData.firstName,
          lastName: formData.lastName
        },
        acceptanceToken: wompiMerchant?.presigned_acceptance?.acceptance_token,
        acceptPersonalAuth: wompiMerchant?.presigned_personal_data_auth?.acceptance_token,
        customerEmail: formData.email,
        wompiPaymentMethod: wm,
        redirectUrl: `${window.location.origin}/checkout/pse-return?pse=1`
      };

      if (isWompiCard(wm)) {
        paymentPayload.token = token;
        paymentPayload.installments = selectedInstallments || 1;
      }
      if (wm === 'wompi_pse') {
        paymentPayload.pseFinancialInstitutionCode = formData.pseFinancialInstitutionCode;
        paymentPayload.pseUserType = parseInt(formData.pseUserType, 10) || 0;
      }
      if (wm === 'wompi_nequi') {
        paymentPayload.nequiPhone = normalizeNequiPhone10Digits(formData.nequiPhone);
      }

      if (WOMPI_ASYNC_BANK_REDIRECT_METHODS.includes(wm)) {
        console.log('Wompi async redirectUrl enviado:', paymentPayload.redirectUrl);
      }

      let paymentResponse;
      try {
        paymentResponse = await paymentService.createPayment(paymentPayload);
      } catch (payErr) {
        console.error('Error en POST /payments/create (Wompi):', payErr);
        if (WOMPI_ASYNC_BANK_REDIRECT_METHODS.includes(wm)) {
          setPaymentError(
            wm === 'wompi_pse'
              ? 'No pudimos conectar con tu banco. Verifica el banco seleccionado e intenta nuevamente.'
              : 'No pudimos iniciar la pasarela de pago. Intenta nuevamente o revisa los datos.'
          );
        } else if (wm === 'wompi_nequi') {
          const m =
            payErr?.response?.data?.message &&
            typeof payErr.response.data.message === 'string'
              ? payErr.response.data.message
              : '';
          setPaymentError(
            m ||
              'No pudimos iniciar el pago por Nequi. Verifica que tu número sea celular Nequi válido e intenta de nuevo.'
          );
        } else {
          let fallback =
            'No se pudo completar el pago. Intenta de nuevo o revisa tus datos.';
          if (payErr?.response?.data?.message && typeof payErr.response.data.message === 'string') {
            fallback = payErr.response.data.message;
          } else if (payErr?.message) {
            fallback = payErr.message;
          }
          setPaymentError(fallback);
        }
        return;
      }

      if (!paymentResponse?.success) {
        const failMsg =
          wm === 'wompi_pse'
            ? 'No pudimos conectar con tu banco. Verifica el banco seleccionado e intenta nuevamente.'
            : wm === 'wompi_nequi'
              ? paymentResponse?.message ||
                'No pudimos iniciar el pago por Nequi. Verifica el número (10 dígitos, sin +57) e intenta de nuevo.'
              : WOMPI_ASYNC_BANK_REDIRECT_METHODS.includes(wm)
              ? paymentResponse?.message ||
                'No pudimos iniciar la pasarela de pago. Intenta nuevamente.'
              : paymentResponse?.message || 'No se pudo procesar el pago en Wompi.';
        setPaymentError(failMsg);
        return;
      }

      if (wm === 'wompi_nequi') {
        const payData = unwrapSendSuccessPayload(paymentResponse);
        const txnId =
          payData?.transactionId ??
          payData?.transaction_id ??
          payData?.transaction?.id ??
          null;
        const ref =
          payData?.reference ??
          payData?.transaction?.reference ??
          order.orderNumber;
        const snapshot = items.map((item) => ({
          id: item.productId || item.id,
          name: item.name,
          image: item.image || '/images/placeholders/placeholder-product.png',
          quantity: item.quantity,
          unitPrice: item.price,
          selectedSize: item.selectedSize,
          selectedColor: item.selectedColor
        }));
        setNequiAwaiting({
          orderId: order.id,
          orderNumber: order.orderNumber,
          transactionId: txnId != null ? String(txnId).trim() : null,
          reference: ref != null ? String(ref).trim() : String(order.orderNumber || ''),
          total: paymentTotal,
          itemsSnapshot: snapshot
        });
        setNequiAwaitMessage('');
        setPaymentError('');
        return;
      }

      const redirectFromApi = WOMPI_ASYNC_BANK_REDIRECT_METHODS.includes(wm)
        ? resolvePseBankRedirectUrl(paymentResponse)
        : resolveCheckoutWompiRedirectUrl(paymentResponse, false);

      if (redirectFromApi !== '' && /^https?:\/\//i.test(redirectFromApi)) {
        if (WOMPI_ASYNC_BANK_REDIRECT_METHODS.includes(wm)) {
          const root = paymentResponse?.data ?? paymentResponse;
          const transactionId =
            root?.transaction_id ?? root?.transaction?.id ?? null;
          const orderRef = root?.order_ref ?? order.orderNumber ?? null;
          if (transactionId != null && String(transactionId).trim() !== '') {
            writePseReturnBackupStores(transactionId, orderRef);
          }
          console.log('Wompi async response:', JSON.stringify(paymentResponse));
          console.log('Wompi async redirect:', redirectFromApi);
        }
        window.location.href = redirectFromApi;
        return;
      }

      /** PSE y Botón BC obligan URL de pasarela cuando aplica */
      const wompiAsyncRequiresRedirectUrl =
        wm === 'wompi_pse' || wm === 'wompi_bancolombia_transfer';

      if (wompiAsyncRequiresRedirectUrl) {
        setPaymentError(
          'No recibimos el enlace de la pasarela (async_payment_url). Si sigue igual, revisa logs del servidor (payments/create).'
        );
        return;
      }

      const tx = paymentResponse.data?.transaction;

      if (!tx?.status) {
        const missingLink = 'Respuesta de pago incompleta (payments/create). Intenta de nuevo.';
        setPaymentError(missingLink);
        return;
      }

      let extraHint = null;
      if (['wompi_daviplata', 'wompi_bancolombia_transfer'].includes(wm)) {
        extraHint =
          wm === 'wompi_daviplata'
            ? 'Si te abrimos ventana para OTP en Daviplata, complétala y vuelve a esta pestaña.'
            : 'Tu pago puede quedar en proceso hasta confirmarse en el banco o por notificación.';
      }

      setCompletedOrder({
        orderId: order.id,
        orderNumber: order.orderNumber,
        total: paymentTotal,
        paymentStatus: tx.status,
        paymentStatusDetail: extraHint || tx.status_message || null,
        paymentMethodKey: wm,
        paymentMethodLabel: getPaymentMethodLabel(wm),
        itemsSnapshot: items.map((item) => ({
          id: item.productId || item.id,
          name: item.name,
          image: normalizeOrderLineImage(item.image || '/images/placeholders/placeholder-product.png'),
          quantity: item.quantity,
          unitPrice: item.price,
          selectedSize: item.selectedSize,
          selectedColor: item.selectedColor
        })),
        subtotal: calculatedSubtotal,
        shipping: effectiveShipping,
        taxAmount: tax || 0,
        ...buildShippingSnapshotFromCheckoutForm(formData),
        customerEmail: formData.email,
        confirmedAt: new Date().toISOString()
      });

      clearPendingCheckoutOrder();
      setCurrentStep(4);
      setTimeout(() => {
        clearCart();
      }, 100);
    } catch (error) {
      console.error('Error procesando orden:', error);
      
      // Extraer mensaje de error más detallado
      let errorMessage = 'Ocurrió un error al procesar el pago. Intenta nuevamente.';
      
      if (error.response) {
        // Error de respuesta del servidor
        const errorData = error.response.data;
        console.error('📥 Respuesta del servidor:', errorData);
        
        if (errorData?.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          // Si hay errores de validación, mostrar todos o el primero con más detalle
          const validationErrors = errorData.errors.map(err => err.msg || err.message || JSON.stringify(err)).filter(Boolean);
          if (validationErrors.length > 0) {
            errorMessage = validationErrors.length === 1 
              ? validationErrors[0]
              : `Errores de validación: ${validationErrors.join(', ')}`;
          } else {
            errorMessage = errorData.message || 'Error en los datos enviados. Verifica la información.';
          }
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        } else if (error.response.status === 400) {
          errorMessage = 'Error en los datos enviados. Verifica la información.';
        }
        
        // Si hay información adicional en received, mostrarla en consola
        if (errorData?.received) {
          console.error('📋 Datos recibidos por el servidor:', errorData.received);
        }
      } else if (error.request) {
        // Error de red
        errorMessage = 'No se pudo conectar con el servidor. Verifica tu conexión.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      errorMessage = formatMercadoPagoClientError(errorMessage) || errorMessage;

      // Solo ocultar mensajes que son casi solo un enlace de MP (el backend ya envía texto legible en la mayoría de casos)
      if (
        errorMessage &&
        /^https?:\/\//i.test(errorMessage.trim()) &&
        errorMessage.length < 100
      ) {
        errorMessage =
          'No pudimos procesar el pago. Abre la consola del navegador (F12) → pestaña Red → intenta de nuevo y revisa la respuesta de payments/create. Verifica también tu número de documento en el checkout y que la página esté en modo prueba (TEST).';
      }
      
      setPaymentError(errorMessage);
    } finally {
      if (!skipLoadingResetDuringBankRedirect) {
        setLoading(false);
      }
    }
  };

  // ==================== RENDERIZADO DE PASOS ====================

  const renderStep1 = () => (
    <div className="checkout-step">
      <h3>👤 Información Personal</h3>
      <div className="form-grid">
        <div className="form-group">
          <label htmlFor="firstName">Nombre *</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            className={errors.firstName ? 'error' : ''}
            placeholder="Tu nombre"
          />
          {errors.firstName && <span className="error-message">{errors.firstName}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="lastName">Apellido *</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            className={errors.lastName ? 'error' : ''}
            placeholder="Tu apellido"
          />
          {errors.lastName && <span className="error-message">{errors.lastName}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="email">Email *</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={errors.email ? 'error' : ''}
            placeholder="tu@email.com"
          />
          {errors.email && <span className="error-message">{errors.email}</span>}
        </div>

        <div className="form-group full-width">
          <label htmlFor="phone">Teléfono *</label>
          <div className="phone-input-group">
            <select
              id="phoneCountryCode"
              name="phoneCountryCode"
              value={formData.phoneCountryCode}
              onChange={handleInputChange}
              className="phone-country-code"
            >
              <option value="+57">🇨🇴 +57 (Colombia)</option>
              <option value="+1">🇺🇸 +1 (USA)</option>
              <option value="+52">🇲🇽 +52 (México)</option>
              <option value="+34">🇪🇸 +34 (España)</option>
              <option value="+54">🇦🇷 +54 (Argentina)</option>
              <option value="+51">🇵🇪 +51 (Perú)</option>
              <option value="+56">🇨🇱 +56 (Chile)</option>
              <option value="+55">🇧🇷 +55 (Brasil)</option>
            </select>
          <input
            type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
            onChange={handleInputChange}
              className={`phone-number ${errors.phoneNumber ? 'error' : ''}`}
              placeholder="300 123 4567"
          />
        </div>
          {errors.phoneNumber && <span className="error-message">{errors.phoneNumber}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="checkout-identification-type">Tipo de documento *</label>
          <select
            id="checkout-identification-type"
            name="identificationType"
            value={formData.identificationType}
            onChange={handleInputChange}
            className={errors.identificationType ? 'error' : ''}
          >
            <option value="CC">Cédula de Ciudadanía (CC)</option>
            <option value="NIT">NIT</option>
            <option value="CE">Cédula de Extranjería (CE)</option>
            <option value="TI">Tarjeta de Identidad (TI)</option>
            <option value="PP">Pasaporte (PP)</option>
          </select>
          {errors.identificationType && <span className="error-message">{errors.identificationType}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="checkout-identification-number">Número de documento *</label>
          <input
            type="text"
            id="checkout-identification-number"
            name="identificationNumber"
            value={formData.identificationNumber}
            onChange={handleInputChange}
            className={errors.identificationNumber ? 'error' : ''}
            placeholder={
              formData.identificationType === 'CC' ? 'Ej: 1234567890' :
              formData.identificationType === 'NIT' ? '900123456-7' :
              formData.identificationType === 'CE' ? 'CE123456' :
              formData.identificationType === 'TI' ? '1234567890' :
              formData.identificationType === 'PP' ? 'PP123456' :
              'Número'
            }
          />
          {errors.identificationNumber && <span className="error-message">{errors.identificationNumber}</span>}
        </div>

      </div>
    </div>
  );

  const renderStep2 = () => {
    // Verificar si el país seleccionado está disponible
    const isCountryAvailable = availableCountries.some(c => c.name === formData.country);
    
    return (
    <div className="checkout-step">
      <h3>📍 Dirección de Envío</h3>
      <div className="form-grid">
        {/* País primero */}
        <div className="form-group">
          <label htmlFor="country">País *</label>
          <select
            id="country"
            name="country"
            value={availableCountries.find(c => c.name === formData.country)?.code || 'CO'}
            onChange={handleCountryChange}
            className={errors.country ? 'error' : ''}
            required
          >
            {availableCountries.map(country => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </select>
          {errors.country && <span className="error-message">{errors.country}</span>}
          {!isCountryAvailable && formData.country && (
            <div className="shipping-notice unavailable">
              <i className="fas fa-exclamation-triangle"></i>
              <span>Actualmente no tenemos envíos a tu país. Puedes visualizar nuestros productos, pero no podrás completar la compra.</span>
            </div>
          )}
          {/* MVP: Solo Colombia - Mensaje informativo */}
          {isCountryAvailable && (
            <div className="shipping-notice info">
              <i className="fas fa-info-circle"></i>
              <span>
                Despachamos a todo el país con las mejores empresas de paquetería.
              </span>
            </div>
          )}
        </div>

        {/* Dirección completa */}
        <div className="form-group full-width">
          <label htmlFor="address">Dirección Completa *</label>
          <input
            type="text"
            id="address"
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            className={errors.address ? 'error' : ''}
            placeholder="Calle, número, apartamento, barrio"
            disabled={!isCountryAvailable}
          />
          {errors.address && <span className="error-message">{errors.address}</span>}
        </div>

        {/* Departamento (solo Colombia) */}
        {formData.country === 'Colombia' && isCountryAvailable && (
        <div className="form-group">
          <label htmlFor="department">Departamento *</label>
          <select
            id="department"
            name="department"
            value={formData.department}
            onChange={handleInputChange}
            className={errors.department ? 'error' : ''}
            disabled={loadingCities}
          >
            <option value="">Selecciona un departamento</option>
            {Array.isArray(departments) && departments
              .filter(dept => dept != null)
              .map((dept, index) => {
                // Asegurar que siempre sea un string
                let deptValue = '';
                if (typeof dept === 'string') {
                  deptValue = dept;
                } else if (dept && typeof dept === 'object') {
                  deptValue = dept.name || dept.department || dept.code || JSON.stringify(dept);
                } else {
                  deptValue = String(dept);
                }
                // Asegurar que no sea un objeto
                if (typeof deptValue !== 'string') {
                  deptValue = String(deptValue);
                }
                const deptKey = `dept-${index}-${deptValue}`;
                return (
                  <option key={deptKey} value={deptValue}>{deptValue}</option>
                );
              })}
          </select>
          {errors.department && <span className="error-message">{errors.department}</span>}
        </div>
        )}

        {/* Municipio (Colombia: lista con código DANE interno) */}
        <div className="form-group">
          <label htmlFor="municipalityDane">{formData.country === 'Colombia' ? 'Municipio *' : 'Ciudad *'}</label>
          {formData.country === 'Colombia' && isCountryAvailable ? (
            <select
              id="municipalityDane"
              name="municipalityDane"
              value={formData.municipalityDane}
              onChange={(e) => {
                const dane = e.target.value;
                const m = municipalities.find((x) => String(x.dane) === String(dane));
                setFormData((prev) => ({
                  ...prev,
                  municipalityDane: dane,
                  city: m ? m.name : ''
                }));
                if (errors.municipalityDane) {
                  setErrors((prev) => ({ ...prev, municipalityDane: '', city: '' }));
                }
              }}
              className={errors.city || errors.municipalityDane ? 'error' : ''}
              disabled={!formData.department || loadingCities}
              required
            >
              <option value="">
                {!formData.department
                  ? 'Primero selecciona un departamento'
                  : loadingCities
                    ? 'Cargando municipios...'
                    : municipalities.length === 0
                      ? 'Sin municipios en listado (amplía data en servidor)'
                      : 'Selecciona un municipio'}
              </option>
              {Array.isArray(municipalities) &&
                municipalities.map((mun) => (
                  <option key={`mun-${mun.dane}`} value={String(mun.dane)}>
                    {mun.name}
                  </option>
                ))}
            </select>
          ) : (
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              className={errors.city ? 'error' : ''}
              placeholder="Ingresa la ciudad"
              required
              disabled={!isCountryAvailable}
            />
          )}
          {errors.city && <span className="error-message">{errors.city}</span>}
          {errors.municipalityDane && (
            <span className="error-message">{errors.municipalityDane}</span>
          )}
        </div>

        {/* Código Postal - Oculto por ahora para Colombia */}
        {/* Se habilitará cuando se agreguen países internacionales */}

        {/* Información Adicional (Apartamento, Casa, Conjunto, etc.) */}
        {isCountryAvailable && (
          <div className="form-group full-width">
            <label htmlFor="shippingAdditionalInfo">
              Información Adicional {formData.country === 'Colombia' ? '(Opcional)' : '(Recomendado)'}
              <small className="field-hint">Apartamento, Casa, Nombre del conjunto, Instrucciones de entrega, etc. (máximo 250 palabras)</small>
            </label>
            <textarea
              id="shippingAdditionalInfo"
              name="shippingAdditionalInfo"
              value={formData.shippingAdditionalInfo}
              onChange={(e) => {
                const text = e.target.value;
                // Limitar a 250 palabras
                const words = text.trim().split(/\s+/).filter(word => word.length > 0);
                if (words.length <= 250) {
                  handleInputChange(e);
                }
              }}
              className={errors.shippingAdditionalInfo ? 'error' : ''}
              placeholder="Ej: Apartamento 402, Casa 15, Conjunto Residencial Los Rosales, Portería, etc."
              rows="3"
              maxLength={1500} // Aproximadamente 250 palabras
              disabled={!isCountryAvailable}
            />
            <small className="field-hint">
              {(() => {
                const wordCount = formData.shippingAdditionalInfo.trim().split(/\s+/).filter(word => word.length > 0).length;
                return `${wordCount}/250 palabras`;
              })()}
            </small>
            {errors.shippingAdditionalInfo && <span className="error-message">{errors.shippingAdditionalInfo}</span>}
          </div>
        )}

        <div className="form-group full-width billing-address-section billing-organic-wrap">
          <div className="billing-organic-header">
            <span className="form-label billing-organic-title">Dirección de facturación</span>
            <p className="field-hint billing-address-hint billing-organic-lead">
              Por defecto la factura electrónica va con la dirección de envío. Si la factura debe ir a otra persona o
              empresa, o si la ubicación de facturación cambia el costo de envío, activá la opción y completá los datos
              del titular (DIAN) y la ubicación.
            </p>
          </div>
          <div className="billing-checkbox-card billing-organic-card">
            <label className="billing-checkbox-label" htmlFor="useDifferentBillingAddress">
              <input
                type="checkbox"
                id="useDifferentBillingAddress"
                name="useDifferentBillingAddress"
                checked={formData.useDifferentBillingAddress}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setFormData((prev) => ({
                    ...prev,
                    useDifferentBillingAddress: checked,
                    ...(checked
                      ? {}
                      : {
                          billingAddress: '',
                          billingCity: '',
                          billingDepartment: '',
                          billingMunicipalityDane: '',
                          billingInvoiceFullName: '',
                          billingIdentificationType: 'CC',
                          billingIdentificationNumber: ''
                        })
                  }));
                  setBillingMunicipalities([]);
                }}
              />
              <span>La dirección de facturación es distinta a la de envío</span>
            </label>
          </div>
        </div>

        {formData.useDifferentBillingAddress && (
          <>
            <div className="billing-invoice-party-card">
              <p className="billing-invoice-party-intro">
                Titular de la factura electrónica (puede ser distinto a quien paga). Estos datos se envían a Wompi y a la
                facturación conforme a la DIAN.
              </p>
              <div className="form-group full-width">
                <label htmlFor="billingInvoiceFullName">Nombre o razón social en la factura *</label>
                <input
                  type="text"
                  id="billingInvoiceFullName"
                  name="billingInvoiceFullName"
                  value={formData.billingInvoiceFullName}
                  onChange={handleInputChange}
                  className={errors.billingInvoiceFullName ? 'error' : ''}
                  placeholder="Ej: María Pérez o Mi Empresa SAS"
                />
                {errors.billingInvoiceFullName && (
                  <span className="error-message">{errors.billingInvoiceFullName}</span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="billingIdentificationType">Tipo de documento *</label>
                <select
                  id="billingIdentificationType"
                  name="billingIdentificationType"
                  value={formData.billingIdentificationType}
                  onChange={handleInputChange}
                  className={errors.billingIdentificationType ? 'error' : ''}
                >
                  <option value="CC">Cédula de Ciudadanía (CC)</option>
                  <option value="NIT">NIT</option>
                  <option value="CE">Cédula de Extranjería (CE)</option>
                  <option value="TI">Tarjeta de Identidad (TI)</option>
                  <option value="PP">Pasaporte (PP)</option>
                </select>
                {errors.billingIdentificationType && (
                  <span className="error-message">{errors.billingIdentificationType}</span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="billingIdentificationNumber">Número de documento *</label>
                <input
                  type="text"
                  id="billingIdentificationNumber"
                  name="billingIdentificationNumber"
                  value={formData.billingIdentificationNumber}
                  onChange={handleInputChange}
                  className={errors.billingIdentificationNumber ? 'error' : ''}
                  placeholder={
                    formData.billingIdentificationType === 'NIT' ? '900123456-7' : '1234567890'
                  }
                />
                {errors.billingIdentificationNumber && (
                  <span className="error-message">{errors.billingIdentificationNumber}</span>
                )}
              </div>
            </div>

            <div className="form-group full-width">
              <label htmlFor="billingAddress">Dirección completa *</label>
              <input
                type="text"
                id="billingAddress"
                name="billingAddress"
                value={formData.billingAddress}
                onChange={handleInputChange}
                className={errors.billingAddress ? 'error' : ''}
                placeholder="Calle, número, apartamento, barrio"
              />
              {errors.billingAddress && <span className="error-message">{errors.billingAddress}</span>}
            </div>

            {formData.country === 'Colombia' && isCountryAvailable && (
              <div className="form-group">
                <label htmlFor="billingDepartment">Departamento *</label>
                <select
                  id="billingDepartment"
                  name="billingDepartment"
                  value={formData.billingDepartment}
                  onChange={handleInputChange}
                  className={errors.billingDepartment ? 'error' : ''}
                >
                  <option value="">Selecciona un departamento</option>
                  {Array.isArray(departments) &&
                    departments
                      .filter((dept) => dept != null)
                      .map((dept, index) => {
                        let deptValue = '';
                        if (typeof dept === 'string') {
                          deptValue = dept;
                        } else if (dept && typeof dept === 'object') {
                          deptValue = dept.name || dept.department || dept.code || JSON.stringify(dept);
                        } else {
                          deptValue = String(dept);
                        }
                        if (typeof deptValue !== 'string') {
                          deptValue = String(deptValue);
                        }
                        const deptKey = `billing-dept-${index}-${deptValue}`;
                        return (
                          <option key={deptKey} value={deptValue}>
                            {deptValue}
                          </option>
                        );
                      })}
                </select>
                {errors.billingDepartment && <span className="error-message">{errors.billingDepartment}</span>}
              </div>
            )}

            {formData.country === 'Colombia' && isCountryAvailable ? (
              <div className="form-group">
                <label htmlFor="billingMunicipalityDane">Municipio *</label>
                <select
                  id="billingMunicipalityDane"
                  name="billingMunicipalityDane"
                  value={formData.billingMunicipalityDane}
                  onChange={(e) => {
                    const dane = e.target.value;
                    const m = billingMunicipalities.find((x) => String(x.dane) === String(dane));
                    setFormData((prev) => ({
                      ...prev,
                      billingMunicipalityDane: dane,
                      billingCity: m ? m.name : ''
                    }));
                    if (errors.billingMunicipalityDane) {
                      setErrors((prev) => ({ ...prev, billingMunicipalityDane: '', billingCity: '' }));
                    }
                  }}
                  className={errors.billingCity || errors.billingMunicipalityDane ? 'error' : ''}
                  disabled={!formData.billingDepartment || loadingBillingMunicipalities}
                  required
                >
                  <option value="">
                    {!formData.billingDepartment
                      ? 'Primero selecciona un departamento'
                      : loadingBillingMunicipalities
                        ? 'Cargando municipios...'
                        : billingMunicipalities.length === 0
                          ? 'Sin municipios en listado'
                          : 'Selecciona un municipio'}
                  </option>
                  {Array.isArray(billingMunicipalities) &&
                    billingMunicipalities.map((mun) => (
                      <option key={`bill-mun-${mun.dane}`} value={String(mun.dane)}>
                        {mun.name}
                      </option>
                    ))}
                </select>
                {errors.billingCity && <span className="error-message">{errors.billingCity}</span>}
                {errors.billingMunicipalityDane && (
                  <span className="error-message">{errors.billingMunicipalityDane}</span>
                )}
              </div>
            ) : (
              <div className="form-group">
                <label htmlFor="billingCity">Ciudad *</label>
                <input
                  type="text"
                  id="billingCity"
                  name="billingCity"
                  value={formData.billingCity}
                  onChange={handleInputChange}
                  className={errors.billingCity ? 'error' : ''}
                  placeholder="Ciudad"
                />
                {errors.billingCity && <span className="error-message">{errors.billingCity}</span>}
              </div>
            )}
          </>
        )}
      </div>

      {shippingCalculating && (
        <div className="loading-shipping">
          <i className="fas fa-spinner fa-spin"></i>
          Calculando costo de envío...
        </div>
      )}
    </div>
  );
  };

  const renderStep3 = () => {
    const pendingOrderId = resolvePendingCheckoutOrderId();
    const pendingOrderRef = resolvePendingCheckoutOrderNumber();
    const showPendingPaymentPanel = Boolean(pendingOrderId) && !nequiAwaiting;

    return (
    <div className="checkout-step">
      <h3>💳 Pago y Facturación</h3>

      {showPendingPaymentPanel ? (
        <div className="checkout-pending-payment-panel" role="status">
          <div className="checkout-pending-payment-panel-head">
            <i className="fas fa-clock" aria-hidden="true" />
            <h4>Pedido pendiente de pago</h4>
          </div>
          {pendingOrderRef ? (
            <p className="checkout-pending-payment-ref">
              Referencia: <strong>{pendingOrderRef}</strong>
            </p>
          ) : null}
          <p className="field-hint">
            Si cancelaste en el banco, el pago no se procesó o falló, usa{' '}
            <strong>Cambiar método de pago</strong> para liberar el inventario y elegir otro medio sin vaciar tu carrito.
          </p>
          <button
            type="button"
            className="btn-secondary-outline checkout-change-payment-btn"
            disabled={paymentMethodChanging || loading || nequiPolling}
            onClick={() => handleChangePaymentMethod()}
          >
            {paymentMethodChanging ? (
              <>
                <i className="fas fa-spinner fa-spin" aria-hidden="true" /> Liberando inventario…
              </>
            ) : (
              'Cambiar método de pago'
            )}
          </button>
        </div>
      ) : null}

      {nequiAwaiting ? (
        <div
          className="billing-section nequi-await-panel"
          style={{
            marginTop: '1rem',
            padding: '1.25rem',
            borderRadius: '8px',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            background: 'rgba(76, 175, 147, 0.07)'
          }}
        >
          <h4 style={{ marginTop: 0 }}>Revisa tu app Nequi</h4>
          <p style={{ marginBottom: '0.75rem' }}>
            Ya creamos tu pedido y enviamos la solicitud de pago a Nequi (<strong>Wompi</strong>).{' '}
            <strong>Aprueba el pago en la aplicación</strong> cuando recibas la notificación push.
          </p>
          <div style={{ marginBottom: '1rem' }}>
            <p style={{ margin: '0.25rem 0' }}>
              <strong>Referencia del pedido:</strong> {nequiAwaiting.reference}
            </p>
            {nequiAwaiting.transactionId ? (
              <p style={{ margin: '0.25rem 0' }}>
                <strong>ID transacción Wompi:</strong> {nequiAwaiting.transactionId}
              </p>
            ) : null}
          </div>
          <p className="field-hint" style={{ marginBottom: '1rem' }}>
            No mostramos &quot;pedido confirmado&quot; como pagado hasta que Wompi confirme el cobro (por ejemplo vía
            webhook al servidor). Usa el botón cuando hayas aceptado en Nequi.
          </p>
          <button
            type="button"
            className="btn-primary"
            disabled={nequiPolling}
            onClick={handleNequiPaidCheckClick}
          >
            {nequiPolling ? (
              <>
                <i className="fas fa-spinner fa-spin" /> Consultando…
              </>
            ) : (
              'Ya pagué'
            )}
          </button>
          <button
            type="button"
            className="btn-secondary-outline"
            style={{ marginLeft: '0.75rem' }}
            disabled={nequiPolling || paymentMethodChanging}
            onClick={handleNequiChangePaymentMethod}
          >
            Cambiar método de pago
          </button>
          {nequiAwaitMessage ? (
            <p className="field-hint" style={{ marginTop: '1rem' }} role="status">
              {nequiAwaitMessage}
            </p>
          ) : null}
          {paymentError ? (
            <div className="payment-error-message" style={{ marginTop: '1rem' }}>
              <i className="fas fa-exclamation-circle" />
              {paymentError}
            </div>
          ) : null}
        </div>
      ) : (
        <>
      {/* Métodos de pago */}
      <div className="payment-methods">
        <h4>Método de Pago</h4>
        <div className="payment-processor-info" style={{ marginBottom: '0.75rem' }}>
          <i className="fas fa-shield-alt"></i>
          <p>Todos los pagos son procesados, controlados y aprobados por <strong>Wompi</strong></p>
        </div>
        <div className="payment-methods-grid">
          <label className="payment-method-option">
            <input
              type="radio"
              name="wompiPaymentMethod"
              value="wompi_card_credit"
              checked={formData.wompiPaymentMethod === 'wompi_card_credit'}
              onChange={handleInputChange}
            />
            <div className="payment-method-content">
              <i className="fas fa-credit-card"></i>
              <div>
                <strong>Tarjeta Crédito</strong>
              </div>
            </div>
          </label>

          <label className="payment-method-option">
            <input
              type="radio"
              name="wompiPaymentMethod"
              value="wompi_card_debit"
              checked={formData.wompiPaymentMethod === 'wompi_card_debit'}
              onChange={handleInputChange}
            />
            <div className="payment-method-content">
              <i className="fas fa-credit-card"></i>
              <div>
                <strong>Tarjeta Débito</strong>
              </div>
            </div>
          </label>

          <label className="payment-method-option">
            <input
              type="radio"
              name="wompiPaymentMethod"
              value="wompi_pse"
              checked={formData.wompiPaymentMethod === 'wompi_pse'}
              onChange={handleInputChange}
            />
            <div className="payment-method-content">
              <i className="fas fa-university"></i>
              <div>
                <strong>PSE</strong>
              </div>
            </div>
          </label>

          <label className="payment-method-option">
            <input
              type="radio"
              name="wompiPaymentMethod"
              value="wompi_nequi"
              checked={formData.wompiPaymentMethod === 'wompi_nequi'}
              onChange={handleInputChange}
            />
            <div className="payment-method-content">
              <i className="fas fa-mobile-alt"></i>
              <div>
                <strong>Nequi</strong>
              </div>
            </div>
          </label>

          <label className="payment-method-option">
            <input
              type="radio"
              name="wompiPaymentMethod"
              value="wompi_bancolombia_transfer"
              checked={formData.wompiPaymentMethod === 'wompi_bancolombia_transfer'}
              onChange={handleInputChange}
            />
            <div className="payment-method-content">
              <i className="fas fa-university"></i>
              <div>
                <strong>Botón Bancolombia</strong>
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Formulario de tarjeta solo para crédito y débito */}
      {isWompiCard(formData.wompiPaymentMethod) && (
        <div className="card-form-wrapper">
          <h4>Datos de la Tarjeta</h4>

          <div className="mp-card-form-container">
            <div id="mp-card-form">
              {(formData.identificationType && String(formData.identificationNumber || '').trim()) ? (
                <div className="mp-field-grid">
                  <div className="mp-field full-width">
                    <p className="checkout-doc-inherited" role="status">
                      Documento del titular:{' '}
                      <strong>{formData.identificationType} {String(formData.identificationNumber).trim()}</strong>
                    </p>
                  </div>
                </div>
              ) : (
                <div className="mp-field-grid">
                  <div className="mp-field">
                    <label htmlFor="mp-form-identification-type">Tipo de identificación del titular *</label>
                    <select
                      id="mp-form-identification-type"
                      name="identificationType"
                      value={formData.identificationType}
                      onChange={handleInputChange}
                      className={errors.identificationType ? 'error' : ''}
                    >
                      <option value="">Selecciona…</option>
                      <option value="CC">Cédula de Ciudadanía (CC)</option>
                      <option value="NIT">NIT</option>
                      <option value="CE">Cédula de Extranjería (CE)</option>
                      <option value="TI">Tarjeta de Identidad (TI)</option>
                      <option value="PP">Pasaporte (PP)</option>
                    </select>
                    {errors.identificationType && <span className="error-message">{errors.identificationType}</span>}
                  </div>
                  <div className="mp-field">
                    <label htmlFor="mp-form-identification-number">Número de documento *</label>
                    <input
                      type="text"
                      id="mp-form-identification-number"
                      name="identificationNumber"
                      value={formData.identificationNumber}
                      onChange={handleInputChange}
                      className={errors.identificationNumber ? 'error' : ''}
                      placeholder={
                        formData.identificationType === 'CC' ? 'Ej: 1234567890' :
                        formData.identificationType === 'NIT' ? '900123456-7' :
                        formData.identificationType === 'CE' ? 'CE123456' :
                        formData.identificationType === 'TI' ? '1234567890' :
                        'PP123456'
                      }
                    />
                    {errors.identificationNumber && <span className="error-message">{errors.identificationNumber}</span>}
                  </div>
                </div>
              )}
              <div className="mp-field-grid">
                <div className="mp-field">
                  <label htmlFor="form-cardholder-name">Nombre en la tarjeta *</label>
                  <input 
                    type="text" 
                    id="form-cardholder-name" 
                    placeholder="Nombre como figura en la tarjeta"
                    required
                  />
                </div>
                <div className="mp-field">
                  <label htmlFor="form-cardholder-email">Email del titular *</label>
                  <input 
                    type="email" 
                    id="form-cardholder-email" 
                    defaultValue={formData.email}
                    placeholder="tu@email.com"
                    required
                  />
                  <small className="field-hint">Email donde recibirás la confirmación del pago</small>
                </div>
              </div>

              <div className="mp-field-grid">
                <div className="mp-field">
                  <label htmlFor="form-card-number">Número de tarjeta *</label>
                  <input 
                    type="text" 
                    id="form-card-number" 
                    placeholder="1234 5678 9012 3456"
                    inputMode="numeric"
                    required
                    maxLength={23}
                    onKeyDown={(e) => {
                      // Permitir teclas de control (backspace, delete, tab, etc.)
                      if (e.key === 'Backspace' || e.key === 'Delete' || e.key === 'Tab' || 
                          e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown' ||
                          e.ctrlKey || e.metaKey) {
                        return;
                      }
                      
                      // Si ya hay 19 dígitos (sin espacios), prevenir más entrada
                      const currentValue = e.target.value.replace(/\s/g, '');
                      if (currentValue.length >= 19 && /[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pastedText = (e.clipboardData || window.clipboardData).getData('text');
                      // Solo permitir números
                      let value = pastedText.replace(/\D/g, '');
                      
                      // Limitar a 19 dígitos máximo
                      if (value.length > 19) {
                        value = value.substring(0, 19);
                      }
                      
                      // Formatear con espacios cada 4 dígitos
                      value = value.match(/.{1,4}/g)?.join(' ') || value;
                      
                      e.target.value = value;
                      // Disparar evento onChange manualmente
                      const event = new Event('input', { bubbles: true });
                      e.target.dispatchEvent(event);
                    }}
                    onChange={(e) => {
                      // Solo permitir números y espacios, formatear automáticamente
                      let value = e.target.value.replace(/\D/g, '');
                      
                      // Limitar a 19 dígitos máximo (sin espacios)
                      if (value.length > 19) {
                        value = value.substring(0, 19);
                      }
                      
                      // Agregar espacios cada 4 dígitos
                      value = value.match(/.{1,4}/g)?.join(' ') || value;
                      
                      // Limitar a 23 caracteres máximo (19 dígitos + 4 espacios)
                      if (value.length > 23) {
                        value = value.substring(0, 23);
                      }
                      
                      e.target.value = value;
                    }}
                    onBlur={(e) => {
                      // Validar al perder el foco
                      const cardNumber = e.target.value.replace(/\s/g, '');
                      if (cardNumber.length > 0 && (cardNumber.length < 13 || cardNumber.length > 19)) {
                        e.target.setCustomValidity('El número de tarjeta debe tener entre 13 y 19 dígitos');
                        e.target.reportValidity();
                      } else {
                        e.target.setCustomValidity('');
                      }
                    }}
                  />
                  <small className="field-hint">Ingresa los 13-19 dígitos de tu tarjeta</small>
                </div>
                <div className="mp-field">
                  <label htmlFor="form-card-expiration-date">Vencimiento (MMAA) *</label>
                  <input 
                    type="text" 
                    id="form-card-expiration-date" 
                    maxLength="4"
                    placeholder="MMAA"
                    inputMode="numeric"
                    pattern="[0-9]{4}"
                    required
                    onChange={(e) => {
                      // Solo permitir números
                      const value = e.target.value.replace(/\D/g, '');
                      e.target.value = value;
                    }}
                  />
                  <small className="field-hint">Formato: MMAA, fecha futura (ej: 1130). Prueba MP (CO): 5031 7557 3453 0604, APRO, CVV 123.</small>
                </div>
                <div className="mp-field">
                  <label htmlFor="form-card-security-code">
                    Código de seguridad
                    <button 
                      type="button" 
                      className="cvv-help-btn" 
                      title="¿Dónde está el CVV?"
                      onClick={(e) => {
                        e.preventDefault();
                        alert('El CVV es el código de 3 o 4 dígitos ubicado en el reverso de tu tarjeta (o en el frente para American Express).');
                      }}
                    >
                      <i className="fas fa-question-circle"></i>
                    </button>
                  </label>
                  <input 
                    type="text" 
                    id="form-card-security-code" 
                    maxLength="4"
                    placeholder="123"
                    inputMode="numeric"
                    pattern="[0-9]{3,4}"
                    required
                    onChange={(e) => {
                      // Solo permitir números
                      const value = e.target.value.replace(/\D/g, '');
                      e.target.value = value;
                    }}
                  />
                  <small className="field-hint">3 o 4 dígitos en el reverso de tu tarjeta</small>
                </div>
              </div>

              <div className="mp-field-grid">
                <div className="mp-field">
                  <label htmlFor="installments-select">
                    Cuotas
                    {loadingInstallments && (
                      <i className="fas fa-spinner fa-spin" style={{ marginLeft: '0.5rem', fontSize: '0.8rem' }}></i>
                    )}
                  </label>
                  <select 
                    id="installments-select"
                    value={selectedInstallments}
                    onChange={(e) => {
                      const newValue = Number(e.target.value);
                      setSelectedInstallments(newValue);
                      console.log('Cuotas seleccionadas:', newValue);
                    }}
                    className="form-control"
                    disabled={loadingInstallments}
                  >
                    {availableInstallments.length > 0 ? (
                      availableInstallments.map(option => {
                        // Mercado Pago devuelve:
                        // - installment_amount: monto por cuota
                        // - total_amount: total a pagar (puede incluir intereses si installment_rate > 0)
                        // - installment_rate: tasa de interés (0 = sin interés)
                        
                        // Para 1 cuota, SIEMPRE usar el paymentTotal (subtotal + shipping, sin tax)
                        const correctTotal = paymentTotal;
                        if (option.installments === 1) {
                          return (
                            <option key={option.installments} value={option.installments}>
                              {`1 cuota (sin interés) - ${formatPrice(correctTotal)}`}
                            </option>
                          );
                        }
                        
                        // Para múltiples cuotas, calcular usando los datos de Mercado Pago
                        const installmentAmount = option.installment_amount || (correctTotal / option.installments);
                        // Si Mercado Pago devuelve total_amount, usarlo; si no, calcular
                        const totalToPay = option.total_amount || (installmentAmount * option.installments);
                        const hasInterest = option.installment_rate > 0 && option.installments > 1;
                        
                        console.log(`Cuota ${option.installments}:`, {
                          installment_amount: option.installment_amount,
                          total_amount: option.total_amount,
                          installment_rate: option.installment_rate,
                          calculated_totalToPay: totalToPay,
                          payment_total: correctTotal,
                          hasInterest
                        });
                        
                        return (
                          <option key={option.installments} value={option.installments}>
                            {`${option.installments} cuotas${hasInterest ? ' con interés' : ' sin interés'} - ${formatPrice(installmentAmount)} c/u${hasInterest ? ` (Total: ${formatPrice(totalToPay)})` : ` (Total: ${formatPrice(correctTotal)})`}`}
                          </option>
                        );
                      })
                    ) : (
                      <option value={1}>1 cuota (sin interés) - {formatPrice(paymentTotal)}</option>
                    )}
                  </select>
                  <small className="field-hint">
                    {loadingInstallments 
                      ? 'Obteniendo opciones de cuotas...'
                      : availableInstallments.length > 0
                        ? (() => {
                            const selected = availableInstallments.find(i => i.installments === selectedInstallments);
                            if (selected) {
                              const correctTotal = paymentTotal;
                              if (selectedInstallments === 1) {
                                // Para 1 cuota, siempre usar el paymentTotal (subtotal + shipping)
                                return `Pago único sin intereses - Total: ${formatPrice(correctTotal)}`;
                              } else {
                                const installmentAmount = selected.installment_amount || (correctTotal / selectedInstallments);
                                // Si Mercado Pago devuelve total_amount, usarlo; si no, calcular
                                const totalToPay = selected.total_amount || (installmentAmount * selectedInstallments);
                                const hasInterest = selected.installment_rate > 0;
                                const difference = totalToPay - correctTotal;
                                
                                console.log('Cuota seleccionada:', {
                                  installments: selectedInstallments,
                                  installment_amount: selected.installment_amount,
                                  total_amount: selected.total_amount,
                                  calculated_totalToPay: totalToPay,
                                  payment_total: correctTotal,
                                  difference,
                                  hasInterest
                                });
                                
                                // Si no hay interés, mostrar el paymentTotal; si hay interés, mostrar el total con interés
                                if (hasInterest && difference > 0) {
                                  return `${selectedInstallments} cuotas de ${formatPrice(installmentAmount)} c/u - Total: ${formatPrice(totalToPay)} (+${formatPrice(difference)} de interés)`;
                                } else {
                                  return `${selectedInstallments} cuotas de ${formatPrice(installmentAmount)} c/u - Total: ${formatPrice(correctTotal)} (sin interés)`;
                                }
                              }
                            }
                            return 'Selecciona una opción de cuotas';
                          })()
                        : 'Ingresa el número de tarjeta para ver las opciones de cuotas'}
                  </small>
                </div>
              </div>

              <input type="hidden" id="form-token" />
            </div>
          </div>
        </div>
      )}

      {formData.wompiPaymentMethod === 'wompi_pse' && (
        <>
          <div className="payment-method-info" style={{ marginTop: '1rem' }}>
            <p>
              Después de crear el pedido te llevaremos al banco para completar el débito desde tu cuenta (<strong>PSE</strong> · <strong>Wompi</strong>).
            </p>
          </div>
          <div className="billing-section" style={{ marginTop: '1rem' }}>
            <div className="form-grid">
              <div className="form-group full-width">
                <label htmlFor="pseFinancialInstitutionCode">Banco PSE *</label>
                <select
                  id="pseFinancialInstitutionCode"
                  name="pseFinancialInstitutionCode"
                  value={formData.pseFinancialInstitutionCode}
                  onChange={handleInputChange}
                  disabled={pseBanksLoading}
                  className={errors.pseFinancialInstitutionCode || pseBanksError ? 'error' : ''}
                  aria-busy={pseBanksLoading}
                >
                  <option value="">
                    {pseBanksLoading ? 'Cargando bancos…' : '— Selecciona tu banco —'}
                  </option>
                  {pseBanks.map((b, idx) => {
                    const code = b.code != null ? String(b.code) : b.financial_institution_code != null ? String(b.financial_institution_code) : '';
                    const label = b.name || b.financial_institution_name || `Banco ${idx}`;
                    return (
                      <option key={code || `b-${idx}`} value={code}>
                        {label}
                      </option>
                    );
                  })}
                </select>
                {pseBanksError && (
                  <span className="error-message" role="alert">
                    {pseBanksError}
                  </span>
                )}
                {errors.pseFinancialInstitutionCode && (
                  <span className="error-message">{errors.pseFinancialInstitutionCode}</span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="pseUserType">Tipo de persona</label>
                <select id="pseUserType" name="pseUserType" value={formData.pseUserType} onChange={handleInputChange}>
                  <option value="0">Natural</option>
                  <option value="1">Jurídica</option>
                </select>
              </div>
            </div>
            <p className="field-hint">El documento del paso 1 se usa como titular del pago PSE.</p>
          </div>
        </>
      )}

      {formData.wompiPaymentMethod === 'wompi_nequi' && (
        <>
          <div className="payment-method-info" style={{ marginTop: '1rem' }}>
            <p>
              Recibirás la solicitud de pago en la app <strong>Nequi</strong> (<strong>Wompi</strong>) en el número que indiques abajo.
              Abre Nequi y acepta cuando recibas la notificación push. Si aparece un enlace externo, complétalo allí y vuelve al comercio.
            </p>
          </div>
          <div className="billing-section" style={{ marginTop: '1rem' }}>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="nequiPhone">Celular Nequi (10 dígitos) *</label>
                <input
                  type="tel"
                  id="nequiPhone"
                  name="nequiPhone"
                  inputMode="numeric"
                  autoComplete="tel"
                  placeholder="Ej: 3001234567"
                  value={formData.nequiPhone}
                  onChange={handleInputChange}
                  className={errors.nequiPhone ? 'error' : ''}
                />
                {errors.nequiPhone && <span className="error-message">{errors.nequiPhone}</span>}
              </div>
            </div>
          </div>
        </>
      )}

      {formData.wompiPaymentMethod === 'wompi_bancolombia_transfer' && (
        <div className="payment-method-info" style={{ marginTop: '1rem' }}>
          <p>
            Tras confirmar, te dirigiremos al flujo seguro de <strong>Bancolombia</strong> (<strong>Botón / transferencias</strong>) vía{' '}
            <strong>Wompi</strong>. Aquí no hace falta ingresar datos bancarios adicionales.
          </p>
        </div>
      )}

      <div className="billing-section wompi-accept-contracts" style={{ marginTop: '1rem' }}>
        {wompiMerchant?.presigned_acceptance?.permalink && wompiMerchant?.presigned_personal_data_auth?.permalink && (
          <div className="field-hint" style={{ marginBottom: '0.75rem' }}>
            Para pagar con Wompi debes aceptar los contratos:{' '}
            <a href={wompiMerchant.presigned_acceptance.permalink} target="_blank" rel="noreferrer">Términos</a>
            {' '}y{' '}
            <a href={wompiMerchant.presigned_personal_data_auth.permalink} target="_blank" rel="noreferrer">Autorización de datos personales</a>.
          </div>
        )}
        <div className="form-grid" style={{ marginBottom: '0.75rem' }}>
          <label className={`checkbox-option-enhanced ${acceptedWompiPolicy ? 'checked' : ''}`} style={{ gridColumn: '1 / -1' }}>
            <div className="checkbox-option-inner">
              <div className="checkbox-square">
                <input
                  type="checkbox"
                  checked={acceptedWompiPolicy}
                  onChange={(e) => setAcceptedWompiPolicy(e.target.checked)}
                />
                <span className="checkbox-indicator">
                  {acceptedWompiPolicy && <i className="fas fa-check"></i>}
                </span>
              </div>
              <div className="checkbox-content">
                <span className="checkbox-title">Acepto los términos de Wompi</span>
              </div>
            </div>
          </label>
          <label className={`checkbox-option-enhanced ${acceptedWompiPersonalData ? 'checked' : ''}`} style={{ gridColumn: '1 / -1' }}>
            <div className="checkbox-option-inner">
              <div className="checkbox-square">
                <input
                  type="checkbox"
                  checked={acceptedWompiPersonalData}
                  onChange={(e) => setAcceptedWompiPersonalData(e.target.checked)}
                />
                <span className="checkbox-indicator">
                  {acceptedWompiPersonalData && <i className="fas fa-check"></i>}
                </span>
              </div>
              <div className="checkbox-content">
                <span className="checkbox-title">Autorizo el tratamiento de mis datos personales en Wompi</span>
              </div>
            </div>
          </label>
        </div>
        {(errors.acceptWompiTerms || errors.acceptWompiData) && (
          <div style={{ gridColumn: '1 / -1' }}>
            {errors.acceptWompiTerms && <span className="error-message">{errors.acceptWompiTerms}</span>}
            {errors.acceptWompiData && (
              <span className="error-message" style={{ display: 'block', marginTop: errors.acceptWompiTerms ? '0.35rem' : 0 }}>
                {errors.acceptWompiData}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Mostrar error de pago o validación */}
      {paymentError && (
        <div className="payment-error-message">
          <i className="fas fa-exclamation-circle"></i>
          {paymentError}
        </div>
      )}
      
      {/* Mostrar errores de validación generales cuando se intenta enviar */}
      {Object.keys(errors).length > 0 && currentStep === 3 && (
        <div className="payment-error-message" style={{ marginTop: '1rem' }}>
          <i className="fas fa-exclamation-triangle"></i>
          Por favor corrige los errores en el formulario antes de continuar.
        </div>
      )}

      {/* Resumen de la orden */}
      <div className="order-review-summary">
        <h4>📋 Resumen de tu Compra</h4>
        <div className="cart-items-list">
          {items.map((item, index) => (
            <div key={index} className="review-item">
              <img src={item.image || '/images/placeholders/placeholder-product.png'} alt={item.name} />
              <div className="item-details">
                <h5>{item.name}</h5>
                <p>Cantidad: {item.quantity}</p>
                {item.selectedSize && <p>Talla: {item.selectedSize}</p>}
                {item.selectedColor && <p>Color: {item.selectedColor}</p>}
    </div>
              <div className="item-price">
                {formatPrice(item.price * item.quantity)}
              </div>
            </div>
          ))}
        </div>

        <div className="order-totals">
          <div className="total-row">
            <span>Subtotal:</span>
            <span>{formatPrice(calculatedSubtotal)}</span>
      </div>
      
          <div className="total-row">
            <span>Impuestos:</span>
            <span>Incluido</span>
          </div>

          <div className="total-row">
            <span>Envío:</span>
            <span>
              {effectiveShipping > 0
                ? formatPrice(effectiveShipping)
                : promoFreeShipping
                  ? 'Gratis'
                  : formatPrice(0)}
            </span>
      </div>

          {appliedCoupon && (
            <div className="total-row">
              <span>Cupón ({appliedCoupon.code}):</span>
              <span>-{formatPrice(couponDiscount)}</span>
            </div>
          )}

          <div className="total-row grand-total">
            <span>Total:</span>
            <span>{formatPrice(paymentTotal)}</span>
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
  };

  const openOrderDetailView = async () => {
    const co = completedOrder || {};
    const oid = co.orderId;
    if (oid != null) {
      setOrderDetailLoading(true);
      setOrderDetailLoadError(false);
      try {
        const merged = await fetchCompletedOrderFromApi(
          oid,
          {
            paymentMethodKey: co.paymentMethodKey,
            paymentMethodLabel: co.paymentMethodLabel,
            paymentStatus: co.paymentStatus || 'paid',
            paymentStatusDetail: co.paymentStatusDetail,
            total: co.total,
            orderId: oid,
            orderNumber: co.orderNumber,
            customerEmail: co.customerEmail || formData.email,
            confirmedAt: co.confirmedAt
          },
          { maxAttempts: 2, delayMs: 400 }
        );
        if (merged) {
          setCompletedOrder(merged);
        } else {
          setOrderDetailLoadError(true);
        }
      } catch (e) {
        console.error('openOrderDetailView:', e);
        setOrderDetailLoadError(true);
      } finally {
        setOrderDetailLoading(false);
      }
    }
    setOrderConfirmationView('detail');
  };

  const renderStep4 = () => {
    const paymentStatus = completedOrder?.paymentStatus || 'pending';
    const co = completedOrder || {};
    const itemsSnap = Array.isArray(co.itemsSnapshot) ? co.itemsSnapshot : [];
    const emailConfirm = co.customerEmail || formData.email;

    const productSidebarLabel =
      itemsSnap.length === 0
        ? '—'
        : itemsSnap.length === 1
          ? itemsSnap[0].name || '1 artículo'
          : `${itemsSnap.length} artículos`;

    let statusConfig = {
      icon: 'fa-check-circle',
      title: '¡Pedido confirmado!',
      message: 'Tu pedido ha sido procesado correctamente.',
      statusText: 'Confirmado',
      thankMsg:
        '¡Recibimos tu pedido! Estamos preparando tus productos naturales Javiland con cuidado para enviarlos a tu dirección. Te avisaremos por correo cuando avance el envío.'
    };

    if (paymentStatus === 'approved' || paymentStatus === 'paid') {
      statusConfig = {
        icon: 'fa-check-circle',
        title: '¡Pedido confirmado!',
        message: 'Tu pedido ha sido procesado correctamente.',
        statusText: 'Confirmado',
        thankMsg:
          '¡Recibimos tu pedido! Estamos preparando tus productos naturales Javiland con cuidado para enviarlos a tu dirección. Te avisaremos por correo cuando avance el envío.'
      };
    } else if (paymentStatus === 'in_process' || paymentStatus === 'pending') {
      statusConfig = {
        icon: 'fa-clock',
        title: 'Pedido registrado',
        message: 'Tu pago está pendiente o en validación. Revisa tu correo para completar los pasos si aplica.',
        statusText: 'Pendiente',
        thankMsg:
          'Ya registramos tu pedido. Cuando el pago se confirme, prepararemos tu envío y te notificaremos a tu correo.'
      };
    } else if (paymentStatus === 'rejected' || paymentStatus === 'failed') {
      statusConfig = {
        icon: 'fa-times-circle',
        title: 'Pago no procesado',
        message: 'No pudimos confirmar el pago. Puedes intentar con otro método.',
        statusText: 'Rechazado',
        thankMsg:
          'Si necesitas ayuda con el pago, escríbenos desde la tienda o a support@javiland.com.'
      };
    }

    const pillClass =
      paymentStatus === 'rejected' || paymentStatus === 'failed'
        ? 'rejected'
        : paymentStatus === 'in_process' || paymentStatus === 'pending'
          ? 'pending'
          : '';

    const showPositiveFlow =
      paymentStatus === 'approved' ||
      paymentStatus === 'paid' ||
      paymentStatus === 'in_process' ||
      paymentStatus === 'pending';

    const fechaStr = formatConfirmedDateCol(co.confirmedAt);
    const subtotalSnap = typeof co.subtotal === 'number' ? co.subtotal : calculatedSubtotal;
    const shippingSnap = typeof co.shipping === 'number' ? co.shipping : shipping;
    const totalSnap = co.total ?? paymentTotal;

    const deliveryLine =
      paymentStatus === 'approved' || paymentStatus === 'paid'
        ? 'Preparación en 1–2 días hábiles. El tiempo de envío depende de tu ciudad; te enviaremos el seguimiento por correo.'
        : 'En cuanto confirmemos tu pago, te indicamos la fecha estimada de entrega.';

    /* ——— Vista resumen (tarjeta central, referencia tipo Laika + marca Javiland) ——— */
    if (orderConfirmationView === 'summary') {
      return (
        <div className="checkout-step order-complete javiland-order-success">
          <div className="javiland-confirm-card">
            <div className="javiland-confirm-check-wrap">
              <i className={`fas ${statusConfig.icon}`} aria-hidden="true" />
            </div>
            <img
              src="/images/Logo_Javiland_Largo.png"
              alt="Javiland"
              className="javiland-confirm-logo"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <img
              src="/images/pedido-confirmado-javiland.png"
              alt=""
              className="javiland-confirm-illustration"
              loading="eager"
              decoding="async"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <h2>{statusConfig.title}</h2>
            <p className="javiland-confirm-sub">{statusConfig.message}</p>
            {co.orderNumber && (
              <p className="javiland-confirm-order-num">
                Pedido <strong>#{co.orderNumber}</strong>
              </p>
            )}
            <div className="javiland-confirm-meta">
              <div>
                <span>Fecha</span>
                <strong>{fechaStr || '—'}</strong>
              </div>
              <div>
                <span>Total</span>
                <strong>{formatPrice(totalSnap)}</strong>
              </div>
              <div>
                <span>Estado</span>
                <strong>{statusConfig.statusText}</strong>
              </div>
            </div>
            <div className="javiland-confirm-actions">
              {showPositiveFlow && (
                <button
                  type="button"
                  className="javiland-btn-primary"
                  onClick={openOrderDetailView}
                  disabled={orderDetailLoading}
                >
                  {orderDetailLoading ? 'Cargando detalle…' : 'Ver detalle del pedido'}
        </button>
              )}
              <button
                type="button"
                className="javiland-btn-primary javiland-btn-outline"
                onClick={() => {
                  window.scrollTo(0, 0);
                  navigateToPage('/');
                }}
              >
                Volver al inicio
        </button>
              {showPositiveFlow && (
                <button type="button" className="javiland-btn-tienda" onClick={navigateToTienda}>
                  <i className="fas fa-store" aria-hidden="true" />
                  Ir a la tienda
                </button>
              )}
              {!showPositiveFlow && (
                <button
                  type="button"
                  className="javiland-btn-primary"
                  onClick={() => {
                    setCurrentStep(3);
                    setPaymentError('');
                  }}
                >
                  Intentar nuevamente
                </button>
              )}
            </div>
      </div>
    </div>
  );
    }

    /* ——— Vista detalle (dos columnas) ——— */
    return (
      <div className="checkout-step order-complete javiland-confirm-detail">
        <button type="button" className="javiland-back-summary" onClick={() => setOrderConfirmationView('summary')}>
          ← Volver al resumen
        </button>
        {co.orderNumber && <h1>Pedido #{co.orderNumber}</h1>}

        <div className="javiland-detail-grid">
          <div className="javiland-detail-main">
            <div className="javiland-detail-card">
              <div className={`javiland-status-pill ${pillClass}`}>{statusConfig.statusText}</div>
              <p className="javiland-delivery-estimate">{deliveryLine}</p>
              <p className="javiland-thank-msg">{statusConfig.thankMsg}</p>
            </div>

            <div className="javiland-detail-card">
              <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Productos</h3>
              {orderDetailLoading ? (
                <p className="javiland-thank-msg muted">Cargando productos de tu pedido…</p>
              ) : itemsSnap.length === 0 ? (
                <p className="javiland-thank-msg">
                  No pudimos cargar el listado aquí. Revisa el correo de confirmación: allí va el detalle de lo
                  comprado. Si necesitas ayuda, visita nuestras{' '}
                  <a href="/preguntas-frecuentes" onClick={(e) => { e.preventDefault(); navigateToPage('/preguntas-frecuentes'); }}>
                    preguntas frecuentes
                  </a>.
                </p>
              ) : (
                itemsSnap.map((line, idx) => (
                  <div key={idx} className="javiland-line-row">
                    <img
                      src={normalizeOrderLineImage(line.image)}
                      alt={line.name || 'Producto'}
                    />
                    <div className="javiland-line-body">
                      <h4>{line.name}</h4>
                      <p className="muted">
                        {line.quantity} {line.quantity === 1 ? 'unidad' : 'unidades'}
                        {line.selectedSize ? ` · Talla ${line.selectedSize}` : ''}
                        {line.selectedColor ? ` · ${line.selectedColor}` : ''}
                      </p>
                    </div>
                    <div className="javiland-line-price">
                      {formatPrice((Number(line.unitPrice) || 0) * (Number(line.quantity) || 1))}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="javiland-detail-card">
              <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Método de pago</h3>
              <div className="javiland-pay-badge">
                <span className="mp-dot">W</span>
                <span>{co.paymentMethodLabel || 'Wompi'}</span>
              </div>
            </div>

            <div className="javiland-detail-card">
              <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Envío</h3>
              <div className="javiland-address-row">
                <i className="fas fa-map-marker-alt" aria-hidden="true" />
                <div className="javiland-ship-detail">
                  {(co.shippingRecipientName || '').trim() !== '' && (
                    <p style={{ margin: '0 0 0.35rem', fontWeight: 700 }}>{co.shippingRecipientName.trim()}</p>
                  )}
                  {(co.shippingIdentification || '').trim() !== '' && (
                    <p className="muted" style={{ margin: '0 0 0.5rem' }}>
                      Documento: {co.shippingIdentification.trim()}
                    </p>
                  )}
                  {(co.shippingAddressLine || '').trim() !== '' &&
                    (co.shippingAddressLine || '').trim() !== '—' && (
                      <p style={{ margin: '0 0 0.35rem' }}>
                        <span className="muted" style={{ fontWeight: 600 }}>
                          Dirección:{' '}
                        </span>
                        {(co.shippingAddressLine || '').trim()}
                      </p>
                    )}
                  {((co.shippingCity || '').trim() !== '' || (co.shippingDepartment || '').trim() !== '') && (
                    <p className="muted" style={{ margin: '0.25rem 0 0' }}>
                      {[co.shippingCity, co.shippingDepartment]
                        .filter((x) => (x || '').trim() !== '')
                        .join(' · ')}
                    </p>
                  )}
                  {(co.shippingCountry || '').trim() !== '' && (
                    <p className="muted" style={{ margin: '0.25rem 0 0' }}>
                      {(co.shippingCountry || '').trim()}
                    </p>
                  )}
                  {(co.shippingZip || '').trim() !== '' && (
                    <p className="muted" style={{ margin: '0.25rem 0 0' }}>
                      Código postal: {(co.shippingZip || '').trim()}
                    </p>
                  )}
                  {(co.shippingExtra || '').trim() !== '' && (
                    <p className="muted" style={{ margin: '0.35rem 0 0' }}>
                      {(co.shippingExtra || '').trim()}
                    </p>
                  )}
                  {!co.shippingRecipientName?.trim?.() &&
                    !((co.shippingAddressLine || '').trim().replace(/^—$/, '')) &&
                    !(co.shippingCity || '').trim?.() &&
                    !(co.shippingDepartment || '').trim?.() &&
                    !(co.shippingCountry || '').trim?.() &&
                    !(co.shippingIdentification || '').trim?.() &&
                    !(co.shippingZip || '').trim?.() &&
                    !(co.shippingExtra || '').trim?.() && (
                      <p className="muted" style={{ margin: 0 }}>
                        —
                      </p>
                    )}
                </div>
              </div>
            </div>
          </div>

          <aside className="javiland-detail-sidebar">
            <h3>Detalle de compra</h3>
            <div className="javiland-sidebar-row javiland-sidebar-row-productos">
              <span className="javiland-sidebar-productos-span">
                Productos <span className="muted">({productSidebarLabel})</span>
              </span>
              <strong>{formatPrice(subtotalSnap)}</strong>
            </div>
            {Number(co.taxAmount) > 0 && (
              <div className="javiland-sidebar-row">
                <span>Impuestos</span>
                <strong>{formatPrice(co.taxAmount)}</strong>
              </div>
            )}
            <div className="javiland-sidebar-row">
              <span>Envío</span>
              <strong>{shippingSnap > 0 ? formatPrice(shippingSnap) : 'Incluido'}</strong>
            </div>
            <div className="javiland-sidebar-total">
              <span>Total</span>
              <span>{formatPrice(totalSnap)}</span>
            </div>
            {emailConfirm && (
              <p className="muted" style={{ marginTop: '1rem', fontSize: '0.85rem' }}>
                Confirmación enviada a <strong>{emailConfirm}</strong>
              </p>
            )}
          </aside>
        </div>

        {(paymentStatus === 'paid' || paymentStatus === 'approved') && (
          <div className="javiland-detail-footer-actions">
            <button
              type="button"
              className="javiland-btn-primary javiland-btn-outline"
              onClick={() => {
                window.scrollTo(0, 0);
                navigateToPage('/');
              }}
            >
              Volver al inicio
            </button>
            <button type="button" className="javiland-btn-tienda" onClick={navigateToTienda}>
              <i className="fas fa-store" aria-hidden="true" />
              Ir a la tienda
            </button>
          </div>
        )}

        <a
          href="/preguntas-frecuentes"
          className="javiland-help-link"
          onClick={(e) => {
            e.preventDefault();
            navigateToPage('/preguntas-frecuentes');
          }}
        >
          ¿Necesitas ayuda?
        </a>
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      default:
        return renderStep1();
    }
  };

  /** Retorno PSE: cargando estado desde API */
  if (pseReturnState?.phase === 'loading') {
    return (
      <div className="checkout checkout-pse-return">
        <div className="container">
          <div className="checkout-step order-complete javiland-order-success" style={{ padding: '2rem 1rem' }}>
            <p style={{ margin: 0, textAlign: 'center', fontSize: '1rem' }}>
              <i className="fas fa-spinner fa-spin" aria-hidden="true" style={{ marginRight: '0.5rem' }} />
              Verificando el resultado del pago con tu banco…
            </p>
          </div>
        </div>
      </div>
    );
  }

  /** Retorno PSE: rechazo, pendiente o error de consulta */
  if (
    pseReturnState?.phase === 'loaded' &&
    ['rejected', 'pending', 'error'].includes(pseReturnState.outcome)
  ) {
    const r = pseReturnState.outcome === 'rejected';
    const err = pseReturnState.outcome === 'error';
    let title = 'Estado del pago PSE';
    let msg =
      typeof pseReturnState.message === 'string'
        ? pseReturnState.message
        : 'No pudimos verificar tu pago. Intenta de nuevo o usa otro método.';
    if (r) {
      title = 'Pago no aprobado';
      msg =
        'Tu pago fue rechazado o cancelado. Liberamos la reserva de inventario; puedes intentar de nuevo con otro método de pago.';
    } else if (pseReturnState.outcome === 'pending') {
      title = 'Pago en proceso';
      msg = 'Tu pago está siendo procesado. Te notificaremos por email.';
    }

    const dismissPseReturn = () => {
      setPseReturnState(null);
      navigateToPage('/tienda');
    };
    const tryCheckoutAgain = () => {
      setPseReturnState(null);
      navigateToPage('/checkout');
    };

    return (
      <div className="checkout checkout-pse-return checkout-order-complete">
        <div className="container">
          <div className="checkout-step order-complete javiland-order-success">
            <div className="javiland-confirm-card">
              <div className="javiland-confirm-check-wrap">
                <i
                  className={`fas ${r ? 'fa-times-circle' : err ? 'fa-exclamation-circle' : 'fa-clock'}`}
                  aria-hidden="true"
                />
              </div>
              <h2 style={{ marginTop: '1rem' }}>{title}</h2>
              <p className="javiland-confirm-sub">{msg}</p>
              {pseReturnState.orderNumber && (
                <p className="javiland-confirm-order-num">
                  Referencia{' '}
                  <strong>{String(pseReturnState.orderNumber)}</strong>
                </p>
              )}
              {pseReturnState.hint && (r || pseReturnState.outcome === 'pending') ? (
                <p className="field-hint" style={{ textAlign: 'center' }}>
                  Estado en pasarela: {String(pseReturnState.hint)}
                </p>
              ) : null}
              <div className="javiland-confirm-actions" style={{ marginTop: '1.25rem' }}>
                {r || err ? (
                  <>
                    <button type="button" className="javiland-btn-primary" onClick={tryCheckoutAgain}>
                      Intentar otro método de pago
                    </button>
                    <button
                      type="button"
                      className="javiland-btn-primary javiland-btn-outline"
                      onClick={dismissPseReturn}
                    >
                      Volver a la tienda
                    </button>
                  </>
                ) : (
                  <button type="button" className="javiland-btn-primary" onClick={dismissPseReturn}>
                    Volver a la tienda
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No ocultar si retorno PSE mostró resultado (ver arriba) o paso 4 (confirmación)
  if (items.length === 0 && currentStep !== 4 && pseReturnState == null) {
    return null;
  }

  return (
    <div className={`checkout${currentStep === 4 ? ' checkout-order-complete' : ''}`}>
      <div className="container">
        {/* Header del checkout */}
        <div className="checkout-header">
          <h1>🛒 Finalizar Compra</h1>
          <p>Completa tu información para recibir tu pedido</p>
        </div>

        <div className="checkout-content">
          <div className="checkout-main">
          {/* Botón Volver al Catálogo */}
          <div className="checkout-top-actions">
            <button
              type="button"
              className="return-catalog-btn-top"
              onClick={() => navigateToPage('/tienda')}
            >
              <i className="fas fa-arrow-left"></i>
              Volver al Catálogo
            </button>
          </div>

          {/* Indicador de pasos */}
          <div className="step-indicator" role="navigation" aria-label="Pasos del checkout">
            <button
              type="button"
              className={`step step-nav ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''} ${currentStep > 1 ? 'step-clickable' : ''}`}
              onClick={() => goToStep(1)}
              disabled={currentStep <= 1}
              aria-current={currentStep === 1 ? 'step' : undefined}
            >
              <span className="step-number">{currentStep > 1 ? '✓' : '1'}</span>
              <span className="step-label">Personal</span>
            </button>
            <button
              type="button"
              className={`step step-nav ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''} ${currentStep > 2 ? 'step-clickable' : ''}`}
              onClick={() => goToStep(2)}
              disabled={currentStep <= 2}
              aria-current={currentStep === 2 ? 'step' : undefined}
            >
              <span className="step-number">{currentStep > 2 ? '✓' : '2'}</span>
              <span className="step-label">Envío</span>
            </button>
            <button
              type="button"
              className={`step step-nav ${currentStep >= 3 ? 'active' : ''} ${currentStep > 3 ? 'completed' : ''} ${currentStep > 3 ? 'step-clickable' : ''}`}
              onClick={() => goToStep(3)}
              disabled={currentStep <= 3}
              aria-current={currentStep === 3 ? 'step' : undefined}
            >
              <span className="step-number">{currentStep > 3 ? '✓' : '3'}</span>
              <span className="step-label">Pago y Facturación</span>
            </button>
            <div className={`step ${currentStep >= 4 ? 'active' : ''}`}>
              <span className="step-number">4</span>
              <span className="step-label">Confirmación</span>
            </div>
          </div>

          {/* Formulario */}
          <div className="checkout-form">
            <form onSubmit={handleSubmit}>
              {renderCurrentStep()}

              {/* Botones de navegación */}
              {currentStep < 4 && (
                <div className="form-actions">
                  {currentStep > 1 && (
                    <button type="button" className="btn-secondary" onClick={prevStep}>
                      <i className="fas fa-arrow-left"></i>
                      Anterior
                    </button>
                  )}
                  
                  {currentStep < 3 ? (
                    <button 
                      type="button" 
                      className="btn-primary" 
                      onClick={nextStep}
                    >
                      Siguiente
                      <i className="fas fa-arrow-right"></i>
                    </button>
                  ) : nequiAwaiting ? (
                    <p className="field-hint" style={{ margin: 0, textAlign: 'center', alignSelf: 'center' }}>
                      Esperando confirmación en Nequi. Usa <strong>«Ya pagué»</strong> en el mensaje anterior; no hagas otro cobro desde aquí.
                    </p>
                  ) : resolvePendingCheckoutOrderId() && paymentError ? (
                    <>
                      <button
                        type="button"
                        className="btn-secondary-outline"
                        disabled={paymentMethodChanging || loading}
                        onClick={() => handleChangePaymentMethod()}
                      >
                        {paymentMethodChanging ? (
                          <>
                            <i className="fas fa-spinner fa-spin" aria-hidden="true" /> Liberando…
                          </>
                        ) : (
                          'Cambiar método de pago'
                        )}
                      </button>
                      <button
                        type="submit"
                        className="btn-primary btn-complete-purchase"
                        disabled={loading || paymentMethodChanging || (isWompiCard(formData.wompiPaymentMethod) && mpLoading)}
                        title="Reintentar pago con el método seleccionado"
                      >
                        {loading ? (
                          <>
                            <i className="fas fa-spinner fa-spin"></i>
                            Procesando...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-lock"></i>
                            Reintentar pago
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <button 
                      type="submit" 
                      className="btn-primary btn-complete-purchase"
                      disabled={loading || (isWompiCard(formData.wompiPaymentMethod) && mpLoading)}
                      title={
                        loading
                          ? 'Procesando...'
                          : isWompiCard(formData.wompiPaymentMethod) && mpLoading
                            ? 'Cargando medio de pago...'
                            : 'Completar compra'
                      }
                    >
                      {loading ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Procesando...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-lock"></i>
                            {mpLoading ? 'Cargando pagos...' : 'Completar Compra'}
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
            </form>
          </div>
          </div> {/* checkout-main */}

          {/* Resumen de la orden (sidebar) */}
          {currentStep < 4 && (
            <div className="order-summary-sidebar">
              <h3>📋 Tu Carrito</h3>
              
              <div className="cart-items-summary">
                {items.map((item, index) => (
                  <div key={index} className="summary-item">
                    <div className="item-info">
                      <img src={item.image || '/images/placeholders/placeholder-product.png'} alt={item.name} />
                      <div>
                        <h4>{item.name}</h4>
                        <p>Cantidad: {item.quantity}</p>
                        {item.selectedSize && <p>Talla: {item.selectedSize}</p>}
                      </div>
                    </div>
                    <span className="item-price">{formatPrice(item.price * item.quantity)}</span>
                  </div>
                ))}
              </div>

              <div className="order-totals">
                <div className="coupon-section" style={{ marginBottom: '12px' }}>
                  <label htmlFor="couponCode" style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>
                    ¿Tienes cupón?
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      id="couponCode"
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Ej: JAVI-XXXX"
                      disabled={couponLoading}
                      style={{ flex: 1 }}
                    />
                    {!appliedCoupon ? (
                      <button type="button" onClick={applyCoupon} disabled={couponLoading}>
                        {couponLoading ? 'Validando...' : 'Aplicar'}
                      </button>
                    ) : (
                      <button type="button" onClick={removeCoupon}>
                        Quitar
                      </button>
                    )}
                  </div>
                  {couponError && <p className="error-message" style={{ marginTop: '6px' }}>{couponError}</p>}
                  {appliedCoupon && (
                    <p style={{ marginTop: '6px', color: '#1b8f3b', fontSize: '0.9rem' }}>
                      Cupón aplicado: <strong>{appliedCoupon.code}</strong>
                    </p>
                  )}
                </div>

                {/* Subtotal */}
                <div className="total-row">
                  <span>Subtotal:</span>
                  <span>{formatPrice(calculatedSubtotal)}</span>
                </div>
                
                {/* Impuestos: Mostrar 19% del subtotal (ya incluidos, solo informativo) */}
                <div className="total-row">
                  <span>Impuestos (19%):</span>
                  <span className="tax-included">{formatPrice(calculatedSubtotal * 0.19)} (Incluido)</span>
                </div>
                
                <div className="total-row">
                  <span>Envío:</span>
                  <span>
                    {effectiveShipping > 0 ? (
                      formatPrice(effectiveShipping)
                    ) : promoFreeShipping ? (
                      <span className="free-shipping">Gratis</span>
                    ) : (
                      formatPrice(0)
                    )}
                  </span>
                </div>

                {appliedCoupon && (
                  <div className="total-row">
                    <span>Cupón ({appliedCoupon.code}):</span>
                    <span>-{formatPrice(couponDiscount)}</span>
                  </div>
                )}

                <div className="total-row grand-total">
                  <span>Total:</span>
                  <span>{formatPrice(paymentTotal)}</span>
                </div>
              </div>

              <div className="security-info">
                <i className="fas fa-shield-alt"></i>
                <p>Pago seguro con encriptación SSL</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {blockingModal ? (
        <div
          className="checkout-blocking-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="checkout-blocking-modal-title"
          onClick={dismissBlockingModal}
        >
          <div
            className="checkout-blocking-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="checkout-blocking-modal-close"
              aria-label="Cerrar"
              onClick={dismissBlockingModal}
            >
              <i className="fas fa-times" aria-hidden="true" />
            </button>
            <div className="checkout-blocking-modal-icon">
              <i className="fas fa-exclamation-circle" aria-hidden="true" />
            </div>
            <h3 id="checkout-blocking-modal-title">{blockingModal.title}</h3>
            <p>{blockingModal.message}</p>
            <button
              type="button"
              className="javiland-btn-primary"
              onClick={dismissBlockingModal}
            >
              Entendido
            </button>
          </div>
        </div>
      ) : null}

      {/* Footer con enlaces a políticas - Usando URLs existentes */}
      <div className="checkout-footer-links">
        <a 
          href="/politicas/shipping.html" 
          target="_blank" 
          rel="noopener noreferrer"
          onClick={(e) => {
            e.preventDefault();
            navigateToPage('/politicas/shipping.html');
          }}
        >
          Política de Envíos
        </a>
        <span>•</span>
        <a 
          href="/politicas/refund.html" 
          target="_blank" 
          rel="noopener noreferrer"
          onClick={(e) => {
            e.preventDefault();
            navigateToPage('/politicas/refund.html');
          }}
        >
          Política de Devoluciones
        </a>
        <span>•</span>
        <a 
          href="/privacy-policy" 
          onClick={(e) => {
            e.preventDefault();
            navigateToPage('/privacy-policy');
          }}
        >
          Política de Privacidad
        </a>
        <span>•</span>
        <a 
          href="/terms-and-conditions" 
          onClick={(e) => {
            e.preventDefault();
            navigateToPage('/terms-and-conditions');
          }}
        >
          Términos de Servicio
        </a>
      </div>
    </div>
  );
};

export default Checkout;
